import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import useEntityUpdates from "hooks/useEntityUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import {
  Box,
  useTheme,
  Typography,
  Divider,
  IconButton,
  Stack,
  useMediaQuery,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  LinearProgress,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import api from "services/api";
import { OVERLAY_NAMES } from "constants/Overlays";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import CloudIcon from "@mui/icons-material/CloudRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { formatAmount } from "helpers/ServiceHelpers";
import { mergeOPIByLanguage, hasMultiLanguageOPI } from "helpers/InvoiceHelpers";
import { getMileageRateForDate } from "helpers/mileageRateHelper";
import Dialog from "components/Dialog";
import dayjs from "dayjs";
import { useAuth } from "../../../contexts/AuthContext";
import { usePageTitle } from "hooks/usePageTitle";
import { Invoice } from "./InvoiceItem";
import { generateInvoicePdfBlob, downloadBlob } from "./invoicePdf";
import CEDatePicker from "components/CEDatePicker";
import { NAVBAR_HEIGHT } from "constants/layout";

const COST_SPLIT_OPTIONS = [
  {
    key: "mileageCost",
    field: "MileageCost",
    label: "Mileage",
    detailsLabel: "Mileage",
  },
  {
    key: "parkingFeesTolls",
    field: "ParkingFeesTolls",
    label: "Parking Fees",
    detailsLabel: "Parking Fees",
  },
  {
    key: "paymentFees",
    field: "PaymentFees",
    label: "Payment Fees",
    detailsLabel: "Online Payment Processing Fees",
  },
  {
    key: "additionalCost",
    field: "AdditionalCost",
    label: "Additional Cost",
    detailsLabel: "Additional Cost",
  },
  {
    key: "additionalLinguistCost",
    field: "AdditionalLinguistCost",
    label: "Additional Linguist Cost",
    detailsLabel: "Additional Linguist Cost",
  },
  {
    key: "equipmentCost",
    field: "EquipmentCost",
    label: "Equipment",
    detailsLabel: "Equipment",
  },
  {
    key: "deliveryCost",
    field: "DeliveryCost",
    label: "Delivery Cost",
    detailsLabel: "Delivery Cost",
  },
  {
    key: "notarizationOrCOTCost",
    field: "NotarizationOrCOTCost",
    label: "Notarization Cost",
    detailsLabel: "Notarization Cost",
  },
  {
    key: "travelTimeCost",
    field: "TravelTimeCost",
    label: "Travel Time",
    detailsLabel: "Travel Time",
  },
];

// Blink animation used to draw attention to the Detailed OPI toggle when the
// invoice has multi-language OPI entries.
const DETAILED_OPI_BLINK = {
  "@keyframes detailedOPIBlink": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.25 },
  },
  animation: "detailedOPIBlink 1s ease-in-out infinite",
};

// Effective word count for a service: explicit WordCount, else parsed from
// the Details text ("- 1,234 words"). Returns null when neither is present.
const getEffectiveWordCount = (service) => {
  const numericWordCount = parseFloat(service?.WordCount);
  if (!Number.isNaN(numericWordCount) && numericWordCount > 0) {
    return numericWordCount;
  }

  const detailsWordCountMatch = service?.Details?.match(
    /-\s*([\d,]+)\s+words\b/i,
  );
  if (!detailsWordCountMatch) return null;

  const parsedFromDetails = parseFloat(
    String(detailsWordCountMatch[1]).replace(/,/g, ""),
  );
  return Number.isNaN(parsedFromDetails) ? null : parsedFromDetails;
};

// Parse the standard Translation Details format:
// {prefix} - {quantity} {unit} - {source} to {targets} {ServiceType}{suffix}
const parseSplitLanguageDetails = (details) => {
  if (typeof details !== "string") return null;

  const detailsMatch = details.match(
    /^(.+)\s+-\s+([\d,]+)\s+(.+?)\s+-\s+(.+?)\s+to\s+(.+?)\s+(Translation|Interpretation|Document Processing|Other Service|Transliteration)(.*)$/,
  );

  if (!detailsMatch) return null;

  return {
    prefix: detailsMatch[1],
    quantityText: `${detailsMatch[2]} ${detailsMatch[3]}`,
    quantityUnit: detailsMatch[3],
    sourceLang: detailsMatch[4],
    targetLangsText: detailsMatch[5],
    serviceType: detailsMatch[6],
    suffix: detailsMatch[7] || "",
  };
};

// Known Other Services type names, used to separate the language
// list from the service type inside the Details text when the
// service's subtype name is unavailable or doesn't match the
// wording used in the Details.
const OTHER_SERVICE_TYPE_NAMES = [
  "Video Synchronization",
  "Desktop Publishing",
  "Cultural Consulting",
  "Language Training",
  "Document Processing",
  "Speaking Engagement",
  "Voiceover",
  "Synchronization",
  "Transcription",
  "Subtitling",
  "Captioning",
  "Dubbing",
  "Localization",
  "Proofreading",
  "Narration",
  "Recording",
  "Consulting",
  "Training",
  "Editing",
  "Review",
  "Design",
  "DTP",
  "Other Service",
];

// Other Services Details format (from OtherServiceNewEdit fillDetails):
// {Requestor} - {Name} - {quantity} {unit} - {Lang1, Lang2, and Lang3} {ServiceType}
// e.g. "Nate Rivera - Tenfold's Educational Videos - 40 minutes - Spanish, Swahili, and Haitian Creole Video Synchronization"
// The language names are parsed from the Details text itself (the
// text before the service type), so the split works even when the
// service has no ServiceTargetLanguages rows saved.
const parseOtherServiceDetails = (details, service) => {
  if (typeof details !== "string") return null;

  const detailsMatch = details.match(
    /^(.+?)\s+-\s+([\d,]+)\s+(.+?)\s+-\s+(.+)$/,
  );
  if (!detailsMatch) return null;

  const lastSegment = detailsMatch[4].trim();

  // Determine the service type: try the service's subtype name and
  // the known type names, and keep the LONGEST match so that e.g.
  // "Video Synchronization" wins over "Synchronization".
  const subtypeName = String(
    service?.ServiceSubtype?.Name || "",
  ).trim();
  const candidates = [...OTHER_SERVICE_TYPE_NAMES];
  if (subtypeName) candidates.push(subtypeName);

  let serviceType = "";
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (
      lastSegment
        .toLowerCase()
        .endsWith(candidate.toLowerCase()) &&
      candidate.length > serviceType.length
    ) {
      serviceType = candidate;
    }
  }

  if (!serviceType) return null;

  const langText = lastSegment
    .slice(0, -serviceType.length)
    .trim();

  // Split the language list ("Lang1, Lang2, and Lang3" format)
  const targetLanguages = langText
    .split(/,\s*and\s+|,\s+|\s+and\s+/)
    .map((lang) => lang.trim())
    .filter((lang) => lang.length > 0);

  if (targetLanguages.length < 2) return null;

  return {
    prefix: detailsMatch[1].trim(),
    quantityText: `${detailsMatch[2]} ${detailsMatch[3]}`,
    quantityUnit: detailsMatch[3],
    sourceLang: "",
    targetLangsText: targetLanguages.join(", "),
    targetLanguages,
    serviceType,
    suffix: "",
    isOtherService: true,
  };
};

// useLongPress — detects a sustained press on the Export PDF buttons.
//
// Mechanic: a normal click exports with the text engine (the button's
// onClick); holding for `delayMs` fires `onTrigger` (the legacy raster
// export) WHILE the finger/mouse is still down. The click that may trail a
// completed hold is then swallowed once via `consumeLongPress()` so the text
// export does not also run. `isHolding` flips true after a short
// `feedbackAfterMs` grace period so quick clicks never flash the hold hint.
const LONG_PRESS_RELEASE_GUARD_MS = 1500;

const useLongPress = ({
  onTrigger,
  delayMs = 3000,
  feedbackAfterMs = 500,
} = {}) => {
  const [isHolding, setIsHolding] = useState(false);
  const holdTimerRef = useRef(null);
  const feedbackTimerRef = useRef(null);
  // Timestamp of the last fired long-press (0 = none) — drives the
  // click-suppression window.
  const firedAtRef = useRef(0);
  // Keep the latest callback without restarting timers on re-render.
  const onTriggerRef = useRef(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  const clearTimers = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (feedbackTimerRef.current) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
  }, []);

  // No timers may outlive the component.
  useEffect(() => clearTimers, [clearTimers]);

  const onPointerDown = useCallback(
    (event) => {
      // Primary button / touch only — ignore right/middle click.
      if (event.button !== 0) return;
      clearTimers();
      feedbackTimerRef.current = setTimeout(() => {
        setIsHolding(true);
      }, feedbackAfterMs);
      holdTimerRef.current = setTimeout(() => {
        holdTimerRef.current = null;
        firedAtRef.current = Date.now();
        setIsHolding(false);
        onTriggerRef.current?.();
      }, delayMs);
    },
    [clearTimers, delayMs, feedbackAfterMs],
  );

  // Releasing before the timer fires = a normal click (the browser's click
  // event drives the text export). Never trigger here — a completed
  // long-press already fired from the timer.
  const onPointerUp = useCallback(() => {
    clearTimers();
    setIsHolding(false);
  }, [clearTimers]);

  // Moving off the button (mouse) or cancelling (touch scroll fires
  // pointercancel) aborts the hold.
  const onPointerLeave = onPointerUp;
  const onPointerCancel = onPointerUp;

  // Android Chrome raises `contextmenu` ~500ms into a touch hold and would
  // otherwise cancel it. These action buttons never need a context menu.
  const onContextMenu = useCallback((event) => {
    event.preventDefault();
  }, []);

  // True exactly once if a long-press fired recently — the click trailing a
  // completed hold must not ALSO start the text export. Safety net: the
  // disabled state (isPrinting) often prevents the click entirely
  // (pointer-events: none), but never rely on a single path.
  const consumeLongPress = useCallback(() => {
    if (
      firedAtRef.current &&
      Date.now() - firedAtRef.current <= LONG_PRESS_RELEASE_GUARD_MS
    ) {
      firedAtRef.current = 0;
      return true;
    }
    return false;
  }, []);

  const handlers = useMemo(
    () => ({
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerCancel,
      onContextMenu,
    }),
    [
      onPointerDown,
      onPointerUp,
      onPointerLeave,
      onPointerCancel,
      onContextMenu,
    ],
  );

  return { handlers, isHolding, consumeLongPress };
};

const InvoiceProfile = () => {
  const [pageTitle, setPageTitle] = useState("Invoice");
  usePageTitle(pageTitle);

  const [tabValue, setTabValue] = React.useState(-1);

  const { currentUser } = useAuth();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const PAGE_PADDING = "1.5cm";

  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state;
  const {
    refreshPage,
    triggerPageRefresh,
    showSnackbar,
    setOverlayName,
    overlayData,
    setOverlayData,
    openDialog,
    handleErrorDialog,
    closeDialog,
  } = useStateContext();

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for this invoice to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to specific invoice so socket joins the entity room for updates
    setCurrentEntity({
      entityType: "Invoice",
      entityId: id,
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [id, setCurrentEntity]);

  // Handler for invoice profile refetch
  const handleInvoiceProfileRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      // Trigger page refresh to reload invoice data
      triggerPageRefresh();
    },
    [triggerPageRefresh],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = `Invoice updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useEntityUpdates({
    entityType: "Invoice",
    entityId: id,
    onUpdate: handleInvoiceProfileRefetch,
  });

  const [, setServiceHistories] = useState([]);

  const OTHER_INVOICES_PAGE_SIZE = 12;
  const HISTORY_FETCH_YEARS_PER_BATCH = 1;
  const EARLIEST_HISTORY_YEAR = 2000;

  const [otherInvoices, setOtherInvoices] = useState([]);
  const [otherInvoicesVisibleCount, setOtherInvoicesVisibleCount] = useState(
    OTHER_INVOICES_PAGE_SIZE,
  );
  const [historyOldestLoadedYear, setHistoryOldestLoadedYear] = useState(null);
  const [hasMoreHistoryYears, setHasMoreHistoryYears] = useState(true);
  const [isOtherInvoicesInitialLoading, setIsOtherInvoicesInitialLoading] =
    useState(false);
  const [isOtherInvoicesLoadingMore, setIsOtherInvoicesLoadingMore] =
    useState(false);
  const [latestInvoiceNumber, setLatestInvoiceNumber] = useState(null);

  const [invoiceData, setInvoiceData] = useState({});
  const [normalItems, setNormalItems] = useState([]);
  const [, setDetailedMileageItems] = useState([]);
  const [splitLanguagesItems, setSplitLanguagesItems] = useState([]);
  const [splitLanguagesMinimumItems, setSplitLanguagesMinimumItems] = useState(
    [],
  );
  const [mileageRates, setMileageRates] = useState([]);
  // Print options state
  const [firstPageRows, setFirstPageRows] = useState(11);
  const [secondPageRows, setSecondPageRows] = useState(11);
  const [rowSpacing, setRowSpacing] = useState(0.09);
  const invoiceRef = useRef(null);
  const invoiceScaleWrapperRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printStatus, setPrintStatus] = useState("Preparing PDF…");
  const [printProgress, setPrintProgress] = useState(0);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [isDraftingEmail, setIsDraftingEmail] = useState(false);

  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [markPaidDate, setMarkPaidDate] = useState(dayjs());
  const [isMarkPaidSubmitting, setIsMarkPaidSubmitting] = useState(false);
  const [isResolvingInvoiceNavigation, setIsResolvingInvoiceNavigation] =
    useState(false);

  // Dynamic cost splitting and detailed OPI state
  const [splitCostSelections, setSplitCostSelections] = useState({});
  const [detailedOPI, setDetailedOPI] = useState(true);
  const [splitLanguages, setSplitLanguages] = useState(true);
  const [splitLanguagesMinimum, setSplitLanguagesMinimum] = useState(false);
  const [splitSimultaneous, setSplitSimultaneous] = useState(false);
  const [showNote, setShowNote] = useState(true);

  // Load persisted Invoice/Print options metadata when the invoice loads.
  // Applied once per invoice id (ref guard) so re-fetches don't clobber
  // in-progress edits. When the invoice has NO saved options, every option is
  // RESET to its default — otherwise navigating X → Y would leave X's options
  // applied to Y (Amine's X → Y → X requirement).
  const appliedOptionsMetadataIdRef = useRef(null);
  useEffect(() => {
    if (!invoiceData?.id) return;
    if (appliedOptionsMetadataIdRef.current === invoiceData.id) return;
    appliedOptionsMetadataIdRef.current = invoiceData.id;

    const meta = invoiceData.OptionsMetadata;
    const hasMeta = meta && typeof meta === "object";

    setSplitCostSelections(
      hasMeta &&
        meta.splitCostSelections &&
        typeof meta.splitCostSelections === "object"
        ? meta.splitCostSelections
        : {},
    );
    setDetailedOPI(
      hasMeta && typeof meta.detailedOPI === "boolean"
        ? meta.detailedOPI
        : true,
    );
    setSplitLanguages(
      hasMeta && typeof meta.splitLanguages === "boolean"
        ? meta.splitLanguages
        : true,
    );
    setSplitLanguagesMinimum(
      hasMeta && typeof meta.splitLanguagesMinimum === "boolean"
        ? meta.splitLanguagesMinimum
        : false,
    );
    setSplitSimultaneous(
      hasMeta && typeof meta.splitSimultaneous === "boolean"
        ? meta.splitSimultaneous
        : false,
    );
    setShowNote(
      hasMeta && typeof meta.showNote === "boolean" ? meta.showNote : true,
    );
    setFirstPageRows(
      hasMeta && Number.isFinite(Number(meta.firstPageRows))
        ? Number(meta.firstPageRows)
        : 11,
    );
    setSecondPageRows(
      hasMeta && Number.isFinite(Number(meta.secondPageRows))
        ? Number(meta.secondPageRows)
        : 11,
    );
    setRowSpacing(
      hasMeta && Number.isFinite(Number(meta.rowSpacing))
        ? Number(meta.rowSpacing)
        : 0.09,
    );
  }, [invoiceData]);

  // Persist Invoice/Print options as metadata whenever any option changes
  // (debounced 800ms so rapid toggles coalesce into one save). Skipped until
  // the load effect above has applied the CURRENT invoice's options — that
  // prevents writing the previous invoice's options into the new one while it
  // is still loading.
  useEffect(() => {
    if (!invoiceData?.id) return;
    if (appliedOptionsMetadataIdRef.current !== invoiceData.id) return;

    const timer = setTimeout(() => {
      api
        .put(`/invoices/${invoiceData.id}`, {
          OptionsMetadata: {
            splitCostSelections,
            detailedOPI,
            splitLanguages,
            splitLanguagesMinimum,
            splitSimultaneous,
            showNote,
            firstPageRows,
            secondPageRows,
            rowSpacing,
          },
        })
        .catch((error) => {
          console.warn(
            "Failed to save invoice options metadata:",
            error?.response?.data?.error || error?.message,
          );
        });
    }, 800);

    return () => clearTimeout(timer);
  }, [
    invoiceData?.id,
    splitCostSelections,
    detailedOPI,
    splitLanguages,
    splitLanguagesMinimum,
    splitSimultaneous,
    showNote,
    firstPageRows,
    secondPageRows,
    rowSpacing,
  ]);

  const availableSplitCostOptions = useMemo(() => {
    const services = Array.isArray(invoiceData?.Services)
      ? invoiceData.Services
      : [];

    return COST_SPLIT_OPTIONS.map((option) => {
      const totalAmount = services.reduce((sum, service) => {
        const parsed = Number(service?.[option.field] || 0);
        return Number.isFinite(parsed) ? sum + parsed : sum;
      }, 0);

      return {
        ...option,
        totalAmount,
      };
    }).filter((option) => option.totalAmount > 0);
  }, [invoiceData?.Services]);

  // Whether any service on this invoice qualifies for language splitting —
  // gates the Split Languages / Split Languages Minimum checkboxes so they
  // can't be toggled when there is nothing to split.
  const canSplitLanguages = useMemo(() => {
    const services = Array.isArray(invoiceData?.Services)
      ? invoiceData.Services
      : [];
    return services.some((service) => {
      const serviceTargetLanguages = service.ServiceTargetLanguages || [];
      const parsedDetails =
        parseSplitLanguageDetails(service.Details) ||
        parseOtherServiceDetails(service.Details, service);
      const hasMultipleLanguages =
        serviceTargetLanguages.length > 1 ||
        (parsedDetails?.targetLanguages?.length || 0) > 1;
      return hasMultipleLanguages;
    });
  }, [invoiceData?.Services]);

  useEffect(() => {
    setSplitCostSelections((prev) => {
      const next = {};
      availableSplitCostOptions.forEach((option) => {
        next[option.key] = prev[option.key] ?? true;
      });
      return next;
    });
  }, [availableSplitCostOptions]);

  const allCostsSelected =
    availableSplitCostOptions.length > 0 &&
    availableSplitCostOptions.every(
      (option) => splitCostSelections[option.key],
    );
  const anyCostSelected = availableSplitCostOptions.some(
    (option) => splitCostSelections[option.key],
  );
  const isMileageSplitSelected = Boolean(
    splitCostSelections.mileageCost || splitCostSelections.parkingFeesTolls,
  );

  const setAllCostsSelected = useCallback(
    (isSelected) => {
      setSplitCostSelections((prev) => {
        const next = { ...prev };
        availableSplitCostOptions.forEach((option) => {
          next[option.key] = isSelected;
        });
        return next;
      });
    },
    [availableSplitCostOptions],
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch mileage rates, units, and invoice data in parallel
        const [
          mileageRatesResponse,
          unitsResponse,
          invoiceResponse,
          latestInvoiceNumberResponse,
        ] = await Promise.all([
          api.get("/mileageRates"),
          api.get("/unitTypes"),
          api.get(`/invoices/${id}`),
          api.get("/invoices/latestInvoiceNumber/"),
        ]);

        // Set mileage rates and units
        const rates = mileageRatesResponse.data || [];
        setMileageRates(rates);
        const unitsList = unitsResponse.data || [];

        // Then set invoice data and generate lists
        const invoice = invoiceResponse.data;
        setInvoiceData(invoice);
        setPageTitle("Invoice: " + invoice.Number);

        const invoiceServices = Array.isArray(invoice?.Services)
          ? invoice.Services
          : [];

        const serviceHistoryResponses = await Promise.all(
          invoiceServices.map((service) =>
            api
              .get(`/services/serviceHistory/${service.id}`)
              .catch(() => ({ data: [] })),
          ),
        );

        const invoiceServiceHistories = serviceHistoryResponses.flatMap(
          (response) => (Array.isArray(response?.data) ? response.data : []),
        );

        setServiceHistories(invoiceServiceHistories);

        if (latestInvoiceNumberResponse?.data?.latestNumber !== undefined) {
          setLatestInvoiceNumber(latestInvoiceNumberResponse.data.latestNumber);
        }

        // Generate both item lists with the mileage rates now available
        // We need to pass the rates to generateItemLists since state might not be updated yet
        const normal = [];
        const detailed = [];

        const latestHistoryByServiceId = new Map();
        (invoiceServiceHistories || []).forEach((history) => {
          const serviceId = Number(history?.ServiceID);
          if (!serviceId) return;
          const current = latestHistoryByServiceId.get(serviceId);
          if (!current) {
            latestHistoryByServiceId.set(serviceId, history);
            return;
          }
          const currentChanged = dayjs(current?.ChangedOn).valueOf() || 0;
          const nextChanged = dayjs(history?.ChangedOn).valueOf() || 0;
          if (nextChanged >= currentChanged) {
            latestHistoryByServiceId.set(serviceId, history);
          }
        });

        const getServiceFieldValue = (service, fieldName) => {
          const direct = service?.[fieldName];
          if (direct !== undefined && direct !== null && direct !== "") {
            return direct;
          }

          const latestHistory = latestHistoryByServiceId.get(
            Number(service?.id),
          );
          if (!latestHistory) return null;

          const toValue = latestHistory?.[`${fieldName}_TO`];
          if (toValue !== undefined && toValue !== null && toValue !== "") {
            return toValue;
          }

          const fromValue = latestHistory?.[`${fieldName}_FROM`];
          if (
            fromValue !== undefined &&
            fromValue !== null &&
            fromValue !== ""
          ) {
            return fromValue;
          }

          return null;
        };

        const parseDurationToDecimal = (durationString) => {
          if (!durationString) return 0;

          if (
            typeof durationString === "object" &&
            typeof durationString.hour === "function" &&
            typeof durationString.minute === "function"
          ) {
            return durationString.hour() + durationString.minute() / 60;
          }

          if (typeof durationString !== "string") return 0;
          const parts = durationString.split(":");
          if (parts.length !== 3) return 0;
          const hours = parseInt(parts[0], 10) || 0;
          const minutes = parseInt(parts[1], 10) || 0;
          const seconds = parseInt(parts[2], 10) || 0;
          return hours + minutes / 60 + seconds / 3600;
        };

        const formatHoursUnit = (decimalHours) => {
          const rounded = Math.round(decimalHours * 100) / 100;
          const formatted =
            rounded % 1 === 0
              ? String(Math.floor(rounded))
              : rounded.toFixed(2);
          const unit = parseFloat(formatted) === 1 ? "hour" : "hours";
          return `${formatted} ${unit}`;
        };

        const applyMinimumDetailsTag = (
          details,
          serviceCategory,
          totalValue,
        ) => {
          if (serviceCategory !== "Translation") return details;
          const numericTotal = parseFloat(totalValue);
          if (Number.isNaN(numericTotal)) return details;
          const isMinimumTotal = Math.abs(numericTotal - 49) < 0.005;
          if (!isMinimumTotal) return details;
          if (typeof details !== "string") return details;
          if (/\bMinimum\b/i.test(details)) return details;
          return `${details} - Minimum`;
        };

        const createSimultaneousBreakdownItems = (
          service,
          baseItem,
          targetTotal,
          paymentFees,
        ) => {
          if (!splitSimultaneous) return null;
          const serviceCategory =
            getServiceFieldValue(service, "ServiceCategory") ||
            service?.ServiceCategory;
          if (serviceCategory !== "Interpretation") {
            return null;
          }

          const modeValue =
            getServiceFieldValue(service, "Mode") || service?.Mode;
          const interpretationTypeValue =
            getServiceFieldValue(service, "InterpretationType") ||
            service?.ServiceSubtype?.Name;
          const hasSimultaneousInType =
            typeof interpretationTypeValue === "string" &&
            /simultaneous/i.test(interpretationTypeValue);

          const simultaneousRateCandidate = parseFloat(
            getServiceFieldValue(service, "ServiceClientRateSimultaneous") ??
              getServiceFieldValue(service, "RateSimultaneous") ??
              service.ServiceClientRateSimultaneous ??
              service.RateSimultaneous ??
              service.ClientRateSimultaneous?.Rate ??
              0,
          );
          const isSimultaneousAssignment =
            modeValue === "Simultaneous" ||
            simultaneousRateCandidate > 0 ||
            hasSimultaneousInType;
          if (!isSimultaneousAssignment) {
            return null;
          }

          const prepDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "PreparationTime") ??
              service.PreparationTime ??
              service.PreparationDuration,
          );
          const schedDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "SchedulingTime") ??
              service.SchedulingTime ??
              service.SchedulingDuration,
          );
          const prepSchedDecimal = prepDecimal + schedDecimal;

          const totalDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "TotalDuration") ??
              service.TotalDuration ??
              service.Duration,
          );
          if (totalDecimal <= 0) {
            return null;
          }

          const roundedTotalDecimal = Math.ceil(totalDecimal * 4) / 4;
          const coreDecimal = Math.max(
            roundedTotalDecimal - prepSchedDecimal,
            0,
          );
          if (coreDecimal <= 0) return null;

          const normalRate = parseFloat(
            getServiceFieldValue(service, "ServiceClientRate") ??
              service.ServiceClientRate ??
              service.ClientRate?.Rate ??
              getServiceFieldValue(service, "Rate") ??
              service.Rate ??
              0,
          );
          const simultaneousRate = parseFloat(
            getServiceFieldValue(service, "ServiceClientRateSimultaneous") ??
              getServiceFieldValue(service, "RateSimultaneous") ??
              service.ServiceClientRateSimultaneous ??
              service.RateSimultaneous ??
              service.ClientRateSimultaneous?.Rate ??
              normalRate,
          );

          if (normalRate <= 0) {
            return null;
          }

          const effectiveSimultaneousRate =
            simultaneousRate > 0 ? simultaneousRate : normalRate;

          const coreRawCharge = effectiveSimultaneousRate * coreDecimal;
          const prepSchedRawCharge = normalRate * prepSchedDecimal;
          const rawTotal = coreRawCharge + prepSchedRawCharge;
          if (rawTotal <= 0) {
            return null;
          }

          const safeTargetTotal = parseFloat(targetTotal) || 0;
          if (safeTargetTotal <= 0) {
            return null;
          }

          const scale = safeTargetTotal / rawTotal;
          const coreTotal = coreRawCharge * scale;
          const prepSchedTotal = prepSchedRawCharge * scale;

          const coreItem = {
            ...baseItem,
            details: `${service.Details} - Simultaneous Time`,
            unit: formatHoursUnit(coreDecimal),
            rate: formatAmount(effectiveSimultaneousRate),
            total: formatAmount(coreTotal.toFixed(2)),
            paymentFees,
          };

          // No preparation/scheduling time — the entire assignment is
          // simultaneous time, so a single line item at the simultaneous
          // rate is all that is needed.
          if (prepSchedDecimal <= 0) {
            return [coreItem];
          }

          const prepSchedItem = {
            ...baseItem,
            details: `${service.Details} - Preparation/Scheduling Time`,
            unit: formatHoursUnit(prepSchedDecimal),
            rate: formatAmount(normalRate),
            total: formatAmount(prepSchedTotal.toFixed(2)),
            paymentFees,
          };

          return [coreItem, prepSchedItem];
        };

        invoice.Services?.forEach((service) => {
          const isInterpretation = service.ServiceCategory === "Interpretation";
          const isSpeakingEngagement = /speaking\s+engagement/i.test(
            String(service.Details || ""),
          );
          const isOPIWithNoDuration =
            service.TotalDuration === "00:00:00" &&
            service.ServiceSubtype?.Name === "OPI";
          const hasUnitCount = service.UnitCount && service.UnitCount > 0;
          const hasMileageCost =
            service.MileageCost && parseFloat(service.MileageCost) > 0;
          const parkingFeesTolls = parseFloat(
            getServiceFieldValue(service, "ParkingFeesTolls") ??
              service.ParkingFeesTolls ??
              0,
          );
          const hasParkingFeesTolls = parkingFeesTolls > 0;
          const accountCharge = parseFloat(service.AccountCharge) || 0;
          const paymentFees = parseFloat(service.PaymentFees) || 0;

          // Get unit type name by UnitTypeID
          const getUnitTypeName = (unitTypeId) => {
            const unitType = unitsList.find((u) => u.id === unitTypeId);
            return unitType ? unitType.Name : "N/A";
          };

          // Pluralize unit name if count > 1
          const pluralizeUnit = (name, count) => {
            return count > 1 ? name + "s" : name;
          };

          // 2-Hour Minimum: interpretations with ApplyTwoHourMinimum show at
          // least 2 hours even when the actual duration is shorter (e.g. a
          // 1-hour assignment billed as 2 hours).
          const rawDurationDecimal = parseDurationToDecimal(
            getServiceFieldValue(service, "TotalDuration") ??
              service.TotalDuration ??
              service.Duration,
          );
          const effectiveDurationDecimal =
            service.ApplyTwoHourMinimum && rawDurationDecimal < 2
              ? 2
              : rawDurationDecimal;

          const baseItem = {
            serviceId: service.id,
            serviceCategory: service.ServiceCategory,
            projectNumber: service.ProjectNumber,
            date: dayjs(service.Date).format("MM-DD-YYYY"),
            details: applyMinimumDetailsTag(
              service.Details,
              service.ServiceCategory,
              accountCharge,
            ),
            unit: isSpeakingEngagement
              ? "N/A"
              : isInterpretation
                ? isOPIWithNoDuration
                  ? "N/A"
                  : convertDurationToDecimal(
                      service.ApplyTwoHourMinimum
                        ? effectiveDurationDecimal
                        : service.TotalDuration,
                    )
                : hasUnitCount
                  ? `${parseFloat(service.UnitCount).toLocaleString("en-US", {
                      maximumFractionDigits: 0,
                    })} ${pluralizeUnit(getUnitTypeName(service.UnitTypeID), service.UnitCount)}`
                  : "N/A",
            rate: isSpeakingEngagement
              ? "N/A"
              : isOPIWithNoDuration
                ? "N/A"
                : isInterpretation
                  ? (() => {
                      const rate =
                        service.ServiceClientRate !== null &&
                        service.ServiceClientRate !== undefined
                          ? service.ServiceClientRate
                          : service.ClientRate?.Rate;
                      if (rate === 0) return "N/A";
                      return rate !== null && rate !== undefined
                        ? formatAmount(rate)
                        : "N/A";
                    })()
                  : !hasUnitCount
                    ? "N/A"
                    : (() => {
                        const rate = service.Rate;
                        if (rate === 0) return "N/A";
                        return rate !== null && rate !== undefined
                          ? formatAmount(rate)
                          : "N/A";
                      })(),
            total: formatAmount(accountCharge),
            accountCharge: accountCharge,
            paymentFees: paymentFees,
            isMileage: false,
            isFee: false,
          };

          const normalBreakdownItems = createSimultaneousBreakdownItems(
            service,
            baseItem,
            accountCharge,
            paymentFees,
          );

          if (normalBreakdownItems) {
            normal.push(...normalBreakdownItems);
          } else {
            normal.push(baseItem);
          }

          if (isInterpretation && (hasMileageCost || hasParkingFeesTolls)) {
            const chargeWithoutTravel =
              accountCharge -
              parseFloat(service.MileageCost || 0) -
              parkingFeesTolls;
            const chargeWithoutTravelFormatted = formatAmount(
              chargeWithoutTravel.toFixed(2),
            );

            const detailedMainBaseItem = {
              ...baseItem,
              total: chargeWithoutTravelFormatted,
              accountCharge: accountCharge,
              paymentFees: paymentFees,
              isMileage: false,
              isFee: false,
            };

            const detailedBreakdownItems = createSimultaneousBreakdownItems(
              service,
              detailedMainBaseItem,
              chargeWithoutTravel,
              paymentFees,
            );

            if (detailedBreakdownItems) {
              detailed.push(...detailedBreakdownItems);
            } else {
              detailed.push(detailedMainBaseItem);
            }

            const mileageRateValue = getMileageRateForDate(
              rates,
              dayjs(service.Date).toDate(),
              0.7,
            );
            const mileageRateAmount = mileageRateValue
              ? formatAmount(
                  mileageRateValue,
                  Math.round(mileageRateValue * 1000) % 10 === 0 ? 2 : 3,
                )
              : "$0.00";

            detailed.push({
              serviceId: service.id,
              serviceCategory: service.ServiceCategory,
              projectNumber: service.ProjectNumber,
              date: dayjs(service.Date).format("MM-DD-YYYY"),
              details: service.Details + " - Mileage",
              unit: (service.MileageRoundtrip || 0) + " miles",
              rate: mileageRateAmount,
              total: formatAmount(service.MileageCost),
              accountCharge: accountCharge,
              paymentFees: 0,
              isMileage: true,
              isFee: false,
            });

            if (hasParkingFeesTolls) {
              detailed.push({
                serviceId: service.id,
                serviceCategory: service.ServiceCategory,
                projectNumber: service.ProjectNumber,
                date: dayjs(service.Date).format("MM-DD-YYYY"),
                details: service.Details + " - Parking Fees",
                unit: "N/A",
                rate: "N/A",
                total: formatAmount(parkingFeesTolls),
                accountCharge: accountCharge,
                paymentFees: 0,
                isMileage: true,
                isFee: false,
              });
            }
          } else {
            const detailedBreakdownItems = createSimultaneousBreakdownItems(
              service,
              baseItem,
              accountCharge,
              paymentFees,
            );

            if (detailedBreakdownItems) {
              detailed.push(...detailedBreakdownItems);
            } else {
              detailed.push(baseItem);
            }
          }
        });

        setNormalItems(normal);
        setDetailedMileageItems(detailed);

        const isMinimumChargeTranslation = (service, totalCharge) => {
          if (service?.ServiceCategory !== "Translation") return false;

          const numericTotal = parseFloat(totalCharge);
          if (Number.isNaN(numericTotal)) return false;

          return Math.abs(numericTotal - 49) < 0.005;
        };

        // Generate split languages items (for WordCount > 245)
        const splitLangs = [];
        invoice.Services?.forEach((service) => {
          const serviceTargetLanguages = service.ServiceTargetLanguages || [];
          const parsedDetails =
            parseSplitLanguageDetails(service.Details) ||
            parseOtherServiceDetails(service.Details, service);
          const hasMultipleLanguages =
            serviceTargetLanguages.length > 1 ||
            (parsedDetails?.targetLanguages?.length || 0) > 1;
          const accountCharge = parseFloat(service.AccountCharge) || 0;
          const paymentFees = parseFloat(service.PaymentFees) || 0;
          const effectiveWordCount = getEffectiveWordCount(service);
          const hasNonWordUnitCount =
            effectiveWordCount === null &&
            Number.parseFloat(service?.UnitCount) > 0;
          const hasNoQuantity =
            effectiveWordCount === null && !hasNonWordUnitCount;
          const hasTargetLanguageFallback =
            serviceTargetLanguages.length > 1;

          if (
            hasMultipleLanguages &&
            ((effectiveWordCount !== null && effectiveWordCount > 245) ||
              hasNonWordUnitCount ||
              (hasNoQuantity && hasTargetLanguageFallback))
          ) {
            if (!parsedDetails) {
              console.warn("[SplitLanguages] details regex did not match", {
                invoiceId: invoice?.id,
                invoiceNumber: invoice?.Number,
                serviceId: service?.id,
                details: service?.Details,
              });
            }

            if (parsedDetails) {
              const {
                prefix,
                sourceLang,
                targetLangsText,
                serviceType,
                suffix,
                isOtherService,
              } = parsedDetails;
              const normalizedQuantityUnit =
                parsedDetails.quantityUnit?.trim().toLowerCase() || "";
              const quantityText =
                normalizedQuantityUnit === "word" ||
                normalizedQuantityUnit === "words"
                  ? `${Math.round(effectiveWordCount || 0).toLocaleString(
                      "en-US",
                      {
                        maximumFractionDigits: 0,
                      },
                    )} words`
                  : parsedDetails.quantityText;

              // Split target languages (handle "Lang1, Lang2, and Lang3" format)
              const targetLanguages = targetLangsText
                .split(/,\s*and\s+|,\s+|\s+and\s+/)
                .map((lang) => lang.trim())
                .filter((lang) => lang.length > 0);

              if (targetLanguages.length > 1) {
                // Calculate charges per language (split total only, not word count)
                const totalCharge = accountCharge;
                const chargePerLanguage = totalCharge / targetLanguages.length;

                // Create an item for each target language
                targetLanguages.forEach((targetLang) => {
                  const hasUnitCount =
                    service.UnitCount && service.UnitCount > 0;
                  const getUnitTypeName = (unitTypeId) => {
                    const unitType = unitsList.find((u) => u.id === unitTypeId);
                    return unitType ? unitType.Name : "N/A";
                  };
                  const pluralizeUnit = (name, count) => {
                    return count > 1 ? name + "s" : name;
                  };
                  const unitDisplay = hasUnitCount
                    ? `${parseFloat(service.UnitCount).toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                      })} ${pluralizeUnit(getUnitTypeName(service.UnitTypeID), service.UnitCount)}`
                    : quantityText;

                  splitLangs.push({
                    serviceId: service.id,
                    serviceCategory: service.ServiceCategory,
                    projectNumber: service.ProjectNumber,
                    date: dayjs(service.Date).format("MM-DD-YYYY"),
                    details: applyMinimumDetailsTag(
                      isOtherService
                        ? `${prefix} - ${quantityText} - ${targetLang} ${serviceType}${suffix}`
                        : `${prefix} - ${quantityText} - ${sourceLang} to ${targetLang} ${serviceType}${suffix}`,
                      service.ServiceCategory,
                      chargePerLanguage,
                    ),
                    unit: unitDisplay,
                    rate: !hasUnitCount
                      ? "N/A"
                      : (() => {
                          const rate = service.Rate;
                          if (rate === 0) return "N/A";
                          return rate !== null && rate !== undefined
                            ? formatAmount(rate)
                            : "N/A";
                        })(),
                    total: formatAmount(chargePerLanguage.toFixed(2)),
                    accountCharge: accountCharge,
                    paymentFees: paymentFees,
                    isMileage: false,
                    isFee: false,
                  });
                });
              } else {
                // Single language or couldn't parse - use normal item
                splitLangs.push(
                  ...normal.filter((item) => item.serviceId === service.id),
                );
              }
            } else if (hasTargetLanguageFallback) {
              // Fallback: split by ServiceTargetLanguages rows when the
              // Details regex doesn't match (e.g. Other Service with no
              // quantity segment in the Details text)
              const totalCharge = accountCharge;
              const chargePerLanguage =
                totalCharge / serviceTargetLanguages.length;
              const serviceTypeName =
                service.ServiceSubtype?.Name ||
                service.ServiceType?.Name ||
                "";

              serviceTargetLanguages.forEach((targetLang) => {
                const langName = targetLang.Language?.Name || "Unknown";
                splitLangs.push({
                  serviceId: service.id,
                  serviceCategory: service.ServiceCategory,
                  projectNumber: service.ProjectNumber,
                  date: dayjs(service.Date).format("MM-DD-YYYY"),
                  details: applyMinimumDetailsTag(
                    `${service.Name || serviceTypeName} - ${langName}${
                      serviceTypeName ? ` ${serviceTypeName}` : ""
                    }`,
                    service.ServiceCategory,
                    chargePerLanguage,
                  ),
                  unit: "N/A",
                  rate: "N/A",
                  total: formatAmount(chargePerLanguage.toFixed(2)),
                  accountCharge: accountCharge,
                  paymentFees: paymentFees,
                  isMileage: false,
                  isFee: false,
                });
              });
            } else {
              // Couldn't parse details - use normal item
              splitLangs.push(
                ...normal.filter((item) => item.serviceId === service.id),
              );
            }
          } else {
            // Not a multi-language service - use normal item
            splitLangs.push(
              ...normal.filter((item) => item.serviceId === service.id),
            );
          }
        });

        setSplitLanguagesItems(splitLangs);

        // Generate split languages minimum items (for WordCount <= 245)
        const splitLangsMinimum = [];
        invoice.Services?.forEach((service) => {
          const serviceTargetLanguages = service.ServiceTargetLanguages || [];
          const parsedDetails =
            parseSplitLanguageDetails(service.Details) ||
            parseOtherServiceDetails(service.Details, service);
          const hasMultipleLanguages =
            serviceTargetLanguages.length > 1 ||
            (parsedDetails?.targetLanguages?.length || 0) > 1;
          const accountCharge = parseFloat(service.AccountCharge) || 0;
          const paymentFees = parseFloat(service.PaymentFees) || 0;
          const effectiveWordCount = getEffectiveWordCount(service);
          const hasNonWordUnitCount =
            effectiveWordCount === null &&
            Number.parseFloat(service?.UnitCount) > 0;
          const hasNoQuantity =
            effectiveWordCount === null && !hasNonWordUnitCount;
          const hasTargetLanguageFallback =
            serviceTargetLanguages.length > 1;
          const isMinimumTranslation = isMinimumChargeTranslation(
            service,
            accountCharge,
          );

          if (
            hasMultipleLanguages &&
            ((effectiveWordCount !== null && effectiveWordCount <= 245) ||
              hasNonWordUnitCount ||
              (hasNoQuantity && hasTargetLanguageFallback)) &&
            !isMinimumTranslation
          ) {
            if (!parsedDetails) {
              console.warn(
                "[SplitLanguagesMinimum] details regex did not match",
                {
                  invoiceId: invoice?.id,
                  invoiceNumber: invoice?.Number,
                  serviceId: service?.id,
                  details: service?.Details,
                },
              );
            }

            if (parsedDetails) {
              const {
                prefix,
                sourceLang,
                targetLangsText,
                serviceType,
                suffix,
                isOtherService,
              } = parsedDetails;
              const normalizedQuantityUnit =
                parsedDetails.quantityUnit?.trim().toLowerCase() || "";
              const quantityText =
                normalizedQuantityUnit === "word" ||
                normalizedQuantityUnit === "words"
                  ? `${Math.round(effectiveWordCount || 0).toLocaleString(
                      "en-US",
                      {
                        maximumFractionDigits: 0,
                      },
                    )} words`
                  : parsedDetails.quantityText;

              // Split target languages (handle "Lang1, Lang2, and Lang3" format)
              const targetLanguages = targetLangsText
                .split(/,\s*and\s+|,\s+|\s+and\s+/)
                .map((lang) => lang.trim())
                .filter((lang) => lang.length > 0);

              if (targetLanguages.length > 1) {
                // Calculate charges per language (split total only, not word count)
                const totalCharge = accountCharge;
                const chargePerLanguage = totalCharge / targetLanguages.length;

                // Create an item for each target language
                targetLanguages.forEach((targetLang) => {
                  const hasUnitCount =
                    service.UnitCount && service.UnitCount > 0;
                  const getUnitTypeName = (unitTypeId) => {
                    const unitType = unitsList.find((u) => u.id === unitTypeId);
                    return unitType ? unitType.Name : "N/A";
                  };
                  const pluralizeUnit = (name, count) => {
                    return count > 1 ? name + "s" : name;
                  };
                  const unitDisplay = hasUnitCount
                    ? `${parseFloat(service.UnitCount).toLocaleString("en-US", {
                        maximumFractionDigits: 0,
                      })} ${pluralizeUnit(getUnitTypeName(service.UnitTypeID), service.UnitCount)}`
                    : quantityText;

                  splitLangsMinimum.push({
                    serviceId: service.id,
                    serviceCategory: service.ServiceCategory,
                    projectNumber: service.ProjectNumber,
                    date: dayjs(service.Date).format("MM-DD-YYYY"),
                    details: applyMinimumDetailsTag(
                      isOtherService
                        ? `${prefix} - ${quantityText} - ${targetLang} ${serviceType}${suffix}`
                        : `${prefix} - ${quantityText} - ${sourceLang} to ${targetLang} ${serviceType}${suffix}`,
                      service.ServiceCategory,
                      chargePerLanguage,
                    ),
                    unit: unitDisplay,
                    rate: !hasUnitCount
                      ? "N/A"
                      : (() => {
                          const rate = service.Rate;
                          if (rate === 0) return "N/A";
                          return rate !== null && rate !== undefined
                            ? formatAmount(rate)
                            : "N/A";
                        })(),
                    total: formatAmount(chargePerLanguage.toFixed(2)),
                    accountCharge: accountCharge,
                    paymentFees: paymentFees,
                    isMileage: false,
                    isFee: false,
                  });
                });
              } else {
                // Single language or couldn't parse - use normal item
                splitLangsMinimum.push(
                  ...normal.filter((item) => item.serviceId === service.id),
                );
              }
            } else if (hasTargetLanguageFallback) {
              // Fallback: split by ServiceTargetLanguages rows when the
              // Details regex doesn't match (e.g. Other Service with no
              // quantity segment in the Details text)
              const totalCharge = accountCharge;
              const chargePerLanguage =
                totalCharge / serviceTargetLanguages.length;
              const serviceTypeName =
                service.ServiceSubtype?.Name ||
                service.ServiceType?.Name ||
                "";

              serviceTargetLanguages.forEach((targetLang) => {
                const langName = targetLang.Language?.Name || "Unknown";
                splitLangsMinimum.push({
                  serviceId: service.id,
                  serviceCategory: service.ServiceCategory,
                  projectNumber: service.ProjectNumber,
                  date: dayjs(service.Date).format("MM-DD-YYYY"),
                  details: applyMinimumDetailsTag(
                    `${service.Name || serviceTypeName} - ${langName}${
                      serviceTypeName ? ` ${serviceTypeName}` : ""
                    }`,
                    service.ServiceCategory,
                    chargePerLanguage,
                  ),
                  unit: "N/A",
                  rate: "N/A",
                  total: formatAmount(chargePerLanguage.toFixed(2)),
                  accountCharge: accountCharge,
                  paymentFees: paymentFees,
                  isMileage: false,
                  isFee: false,
                });
              });
            } else {
              // Couldn't parse details - use normal item
              splitLangsMinimum.push(
                ...normal.filter((item) => item.serviceId === service.id),
              );
            }
          } else {
            // Not a multi-language service - use normal item
            splitLangsMinimum.push(
              ...normal.filter((item) => item.serviceId === service.id),
            );
          }
        });
        setSplitLanguagesMinimumItems(splitLangsMinimum);
      } catch (error) {
        const statusCode = error?.response?.status;

        if (statusCode === 404) {
          showSnackbar(`Invoice #${id} was not found.`, theme, "warning");

          const comingFrom =
            typeof data?.ComingFrom === "string" ? data.ComingFrom : null;
          const fallbackRoute =
            comingFrom && comingFrom !== `/invoices/${id}`
              ? comingFrom
              : "/invoices";

          navigate(fallbackRoute, { replace: true });
          return;
        }

        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [
    data?.ComingFrom,
    id,
    navigate,
    refreshPage,
    showSnackbar,
    splitSimultaneous,
    theme,
  ]);

  const mergeInvoicesById = useCallback((existing, incoming) => {
    const map = new Map();

    [...existing, ...incoming].forEach((invoice) => {
      if (!invoice?.id) return;
      map.set(invoice.id, invoice);
    });

    return Array.from(map.values()).sort((a, b) => {
      const dateA = dayjs(a.Date).valueOf();
      const dateB = dayjs(b.Date).valueOf();

      if (dateA !== dateB) return dateB - dateA;

      const numberA = parseInt(a.Number) || 0;
      const numberB = parseInt(b.Number) || 0;
      return numberB - numberA;
    });
  }, []);

  const fetchOtherInvoicesByYearRange = useCallback(
    async (minYear, maxYear, { append = false, loadingMore = false } = {}) => {
      if (!invoiceData?.ClientID) return;

      if (loadingMore) {
        setIsOtherInvoicesLoadingMore(true);
      } else {
        setIsOtherInvoicesInitialLoading(true);
      }

      try {
        const response = await api.get("/invoices/byClientHistory", {
          params: {
            clientId: invoiceData.ClientID,
            minYear,
            maxYear,
            excludeInvoiceId: id,
          },
        });

        const fetched = Array.isArray(response.data) ? response.data : [];

        setOtherInvoices((prev) => {
          const base = append ? prev : [];
          return mergeInvoicesById(base, fetched);
        });
      } catch (error) {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load other invoices.";
        handleErrorDialog(message, theme);
      } finally {
        if (loadingMore) {
          setIsOtherInvoicesLoadingMore(false);
        } else {
          setIsOtherInvoicesInitialLoading(false);
        }
      }
    },
    [invoiceData?.ClientID, id, mergeInvoicesById, handleErrorDialog, theme],
  );

  useEffect(() => {
    if (!invoiceData?.ClientID) return;

    const currentYear = dayjs().year();
    const initialMinYear = currentYear - 1;

    setOtherInvoices([]);
    setOtherInvoicesVisibleCount(OTHER_INVOICES_PAGE_SIZE);
    setHistoryOldestLoadedYear(initialMinYear);
    setHasMoreHistoryYears(initialMinYear > EARLIEST_HISTORY_YEAR);

    fetchOtherInvoicesByYearRange(initialMinYear, currentYear, {
      append: false,
      loadingMore: false,
    });
  }, [
    invoiceData?.ClientID,
    id,
    fetchOtherInvoicesByYearRange,
    OTHER_INVOICES_PAGE_SIZE,
  ]);

  const loadPreviousYearsInvoices = useCallback(async () => {
    if (
      !hasMoreHistoryYears ||
      isOtherInvoicesLoadingMore ||
      historyOldestLoadedYear === null
    ) {
      return;
    }

    const nextMaxYear = historyOldestLoadedYear - 1;
    const nextMinYear = nextMaxYear - (HISTORY_FETCH_YEARS_PER_BATCH - 1);

    if (nextMaxYear < EARLIEST_HISTORY_YEAR) {
      setHasMoreHistoryYears(false);
      return;
    }

    await fetchOtherInvoicesByYearRange(nextMinYear, nextMaxYear, {
      append: true,
      loadingMore: true,
    });

    setHistoryOldestLoadedYear(nextMinYear);
    setHasMoreHistoryYears(nextMinYear > EARLIEST_HISTORY_YEAR);
  }, [
    hasMoreHistoryYears,
    isOtherInvoicesLoadingMore,
    historyOldestLoadedYear,
    fetchOtherInvoicesByYearRange,
    HISTORY_FETCH_YEARS_PER_BATCH,
  ]);

  const visibleOtherInvoices = useMemo(() => {
    return otherInvoices.slice(0, otherInvoicesVisibleCount);
  }, [otherInvoices, otherInvoicesVisibleCount]);

  const handleOtherInvoicesScroll = useCallback(
    async (event) => {
      const target = event.currentTarget;
      const isNearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

      if (!isNearBottom) return;

      if (otherInvoicesVisibleCount < otherInvoices.length) {
        setOtherInvoicesVisibleCount((prev) =>
          Math.min(prev + OTHER_INVOICES_PAGE_SIZE, otherInvoices.length),
        );
        return;
      }

      if (hasMoreHistoryYears && !isOtherInvoicesLoadingMore) {
        await loadPreviousYearsInvoices();
      }
    },
    [
      otherInvoicesVisibleCount,
      otherInvoices.length,
      hasMoreHistoryYears,
      isOtherInvoicesLoadingMore,
      loadPreviousYearsInvoices,
      OTHER_INVOICES_PAGE_SIZE,
    ],
  );

  // Check if invoice contains OPI services
  const hasOPIServices = useMemo(() => {
    const items = normalItems;
    return items.some((item) => {
      if (item.serviceCategory !== "Interpretation" || !item.details)
        return false;
      return /Phone\s+.+?\s+Interpretation/.test(item.details);
    });
  }, [normalItems]);

  // Check if any OPI service has multiple languages (reuses the same
  // grouping logic as the Detailed OPI merge so the blink stays consistent
  // with what the toggle actually does).
  const hasMultiLangOPI = useMemo(
    () => hasMultiLanguageOPI(normalItems),
    [normalItems],
  );

  // Uncheck Detailed OPI when there are no OPI services
  useEffect(() => {
    if (!hasOPIServices) {
      setDetailedOPI(false);
    }
  }, [hasOPIServices]);

  // Compute final items list based on split costs, split languages settings, and detailedOPI.
  const displayItems = useMemo(() => {
    let baseItems = normalItems;

    const combineSplitLanguageItems = (primaryItems, minimumItems) => {
      const serviceOrder = (
        Array.isArray(invoiceData?.Services) ? invoiceData.Services : []
      )
        .map((service) => Number(service?.id))
        .filter((serviceId) => serviceId > 0);

      if (serviceOrder.length === 0) {
        return primaryItems;
      }

      const buildItemsByService = (items) => {
        const map = new Map();

        items.forEach((item) => {
          const serviceId = Number(item?.serviceId || 0);
          if (!serviceId) return;
          if (!map.has(serviceId)) map.set(serviceId, []);
          map.get(serviceId).push(item);
        });

        return map;
      };

      const primaryByService = buildItemsByService(primaryItems);
      const minimumByService = buildItemsByService(minimumItems);

      const scoreItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) return 0;
        return items.filter((item) => !item?.isFee && !item?.isMileage).length;
      };

      const combined = [];
      serviceOrder.forEach((serviceId) => {
        const primary = primaryByService.get(serviceId) || [];
        const minimum = minimumByService.get(serviceId) || [];

        const primaryScore = scoreItems(primary);
        const minimumScore = scoreItems(minimum);

        if (minimumScore > primaryScore) {
          combined.push(...minimum);
          return;
        }

        if (primary.length > 0) {
          combined.push(...primary);
          return;
        }

        if (minimum.length > 0) {
          combined.push(...minimum);
        }
      });

      return combined;
    };

    // Apply split languages first if enabled
    if (splitLanguages) {
      baseItems = splitLanguagesItems;
    }

    // When both are enabled, combine per-service and keep the more detailed split variant.
    if (splitLanguages && splitLanguagesMinimum) {
      baseItems = combineSplitLanguageItems(
        splitLanguagesItems,
        splitLanguagesMinimumItems,
      );
    } else if (splitLanguagesMinimum) {
      // Apply split languages minimum if enabled by itself
      baseItems = splitLanguagesMinimumItems;
    }

    const itemsWithSplitCosts = (() => {
      const selectedCostOptions = availableSplitCostOptions.filter(
        (option) => splitCostSelections[option.key],
      );
      if (selectedCostOptions.length === 0) {
        return baseItems;
      }

      const parseCurrency = (value) => {
        if (value === null || value === undefined) return 0;
        const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
        return Number.isNaN(parsed) ? 0 : parsed;
      };

      const serviceById = new Map(
        (Array.isArray(invoiceData?.Services) ? invoiceData.Services : [])
          .filter((service) => Number(service?.id) > 0)
          .map((service) => [Number(service.id), service]),
      );

      const selectedCostsByService = new Map();
      serviceById.forEach((service, serviceId) => {
        const selectedCosts = selectedCostOptions
          .map((option) => ({
            ...option,
            amount: Number(service?.[option.field] || 0),
          }))
          .filter((cost) => Number.isFinite(cost.amount) && cost.amount > 0);

        if (selectedCosts.length > 0) {
          selectedCostsByService.set(serviceId, selectedCosts);
        }
      });

      const lastIndexByService = new Map();
      const chargeItemCountByService = new Map();
      const representativeByService = new Map();

      baseItems.forEach((item, index) => {
        if (!item?.serviceId) return;
        lastIndexByService.set(item.serviceId, index);

        if (
          !item.isMileage &&
          !item.isFee &&
          !representativeByService.has(item.serviceId)
        ) {
          representativeByService.set(item.serviceId, item);
        }

        if (!item.isFee && !item.isMileage) {
          const count = chargeItemCountByService.get(item.serviceId) || 0;
          chargeItemCountByService.set(item.serviceId, count + 1);
        }
      });

      const updated = [];

      baseItems.forEach((item, index) => {
        const serviceId = Number(item?.serviceId || 0);
        const selectedCosts = serviceId
          ? selectedCostsByService.get(serviceId) || []
          : [];
        const splitTotal = selectedCosts.reduce(
          (sum, cost) => sum + Number(cost.amount || 0),
          0,
        );
        const count = serviceId
          ? chargeItemCountByService.get(serviceId) || 0
          : 0;
        let nextItem = item;

        if (
          splitTotal > 0 &&
          count > 0 &&
          !item.isFee &&
          !item.isMileage &&
          item.total &&
          item.total !== "N/A"
        ) {
          const totalValue = parseCurrency(item.total);
          const adjustedTotal = Math.max(totalValue - splitTotal / count, 0);
          nextItem = {
            ...item,
            total: formatAmount(adjustedTotal.toFixed(2)),
          };
        }

        updated.push(nextItem);

        const isLastForService =
          serviceId && lastIndexByService.get(serviceId) === index;

        if (isLastForService && selectedCosts.length > 0) {
          const representative = representativeByService.get(serviceId) || item;
          const baseDetails = representative?.details || "";
          const sourceService = serviceById.get(serviceId) || {};

          const buildCostDetails = (detailsLabel) =>
            baseDetails ? `${baseDetails} - ${detailsLabel}` : detailsLabel;

          const getMileageRateAmount = () => {
            const mileageRateValue = getMileageRateForDate(
              mileageRates,
              dayjs(sourceService.Date).toDate(),
              0.7,
            );
            return mileageRateValue
              ? formatAmount(
                  mileageRateValue,
                  Math.round(mileageRateValue * 1000) % 10 === 0 ? 2 : 3,
                )
              : "N/A";
          };

          selectedCosts.forEach((cost) => {
            const isMileageCost = cost.key === "mileageCost";
            const isTravelTimeCost = cost.key === "travelTimeCost";
            const clientRate = Number(
              sourceService.ServiceClientRate ??
                sourceService.ClientRate?.Rate ??
                0,
            );
            const unit = isMileageCost
              ? `${sourceService.MileageRoundtrip || 0} miles`
              : isTravelTimeCost
                ? (() => {
                    const rawDuration = sourceService.TravelTimeDuration;
                    if (typeof rawDuration !== "string" || !rawDuration) {
                      return "N/A";
                    }
                    const parts = rawDuration.split(":");
                    return parts.length >= 2
                      ? `${parts[0]}:${parts[1]}`
                      : rawDuration;
                  })()
                : "N/A";
            const rate = isMileageCost
              ? getMileageRateAmount()
              : isTravelTimeCost && clientRate > 0
                ? formatAmount((clientRate / 2).toFixed(2))
                : "N/A";

            updated.push({
              serviceId: representative?.serviceId || serviceId,
              serviceCategory:
                representative?.serviceCategory || item?.serviceCategory,
              projectNumber:
                representative?.projectNumber || item?.projectNumber,
              date: representative?.date || item?.date,
              details: buildCostDetails(cost.detailsLabel),
              unit,
              rate,
              total: formatAmount(cost.amount.toFixed(2)),
              accountCharge: 0,
              paymentFees: cost.key === "paymentFees" ? cost.amount : 0,
              isMileage: isMileageCost,
              isFee: cost.key === "paymentFees",
            });
          });
        }
      });

      return updated;
    })();

    // Finally apply OPI merging if enabled
    if (detailedOPI) {
      return itemsWithSplitCosts;
    }

    return mergeOPIByLanguage(itemsWithSplitCosts);
  }, [
    splitLanguages,
    splitLanguagesMinimum,
    splitLanguagesItems,
    splitLanguagesMinimumItems,
    normalItems,
    detailedOPI,
    availableSplitCostOptions,
    splitCostSelections,
    invoiceData?.Services,
    mileageRates,
  ]);

  const invoiceTerms = useMemo(() => {
    const baseTerms =
      "In the event of late payment, a 10% late fee will be added after 30 business days of receiving the invoice.";
    const hasPaymentLink =
      typeof invoiceData?.PaymentLink === "string" &&
      invoiceData.PaymentLink.trim().length > 0;

    if (!hasPaymentLink) return baseTerms;

    return `${baseTerms}\nA 5% administrative charge applies to online payments only. Cash, check, and ACH are excluded.`;
  }, [invoiceData?.PaymentLink]);

  // Mirror of InvoiceNew: a payment link is only forced when Credit Card is
  // the client's ONLY payment method. Clients with additional methods can be
  // invoiced without one, so printing must not be blocked for them either.
  const clientHasOnlyCreditCardPaymentMethod = useMemo(() => {
    const methods = invoiceData?.Client?.ClientPaymentMethods;
    if (!Array.isArray(methods) || methods.length !== 1) return false;

    const methodName = methods[0]?.PaymentMethod?.Name;
    return (
      typeof methodName === "string" &&
      methodName.trim().toLowerCase() === "credit card"
    );
  }, [invoiceData?.Client?.ClientPaymentMethods]);

  const invoiceHasPaymentLink = useMemo(
    () =>
      typeof invoiceData?.PaymentLink === "string" &&
      invoiceData.PaymentLink.trim().length > 0,
    [invoiceData?.PaymentLink],
  );

  const isPrintBlockedByMissingPaymentLink =
    clientHasOnlyCreditCardPaymentMethod && !invoiceHasPaymentLink;

  const invoiceClientName =
    invoiceData.ClientName || invoiceData.Client?.Account || "N/A";
  const invoiceClientId =
    invoiceData.ClientID || invoiceData.Client?.id || null;

  // Invoicer/CC come from the FIRST service (documented rule): multi-service
  // invoices can have mixed invoicers, so the first service's invoicer is the
  // billing contact — matches the server's draft-email resolution.
  const firstInvoiceService = invoiceData.Services?.[0];
  const invoiceInvoicer = firstInvoiceService?.Invoicer || null;
  const invoiceCcEmails = (
    firstInvoiceService?.Requestor?.Group?.CCRequestors || []
  )
    .map((cc) => cc?.Email)
    .filter(Boolean);
  const structuredAddressLine1 = (() => {
    const normalized = String(
      invoiceData.Client?.AddressRecord?.AddressLine1 || "",
    ).trim();
    return normalized && normalized.toUpperCase() !== "N/A" ? normalized : "";
  })();
  const structuredAddressLine2 = (() => {
    const normalized = String(
      invoiceData.Client?.AddressRecord?.AddressLine2 || "",
    ).trim();
    return normalized && normalized.toUpperCase() !== "N/A" ? normalized : "";
  })();
  const structuredAddressLine3 = (() => {
    const normalized = String(
      invoiceData.Client?.AddressRecord?.AddressLine3 || "",
    ).trim();
    return normalized && normalized.toUpperCase() !== "N/A" ? normalized : "";
  })();

  // Calculate discount values from invoice data. Amount is stored as the
  // SUBTOTAL (discounts live in the discount fields), so the total shown is
  // the subtotal minus the discount.
  const getDiscountValues = () => {
    let subtotal = parseFloat(invoiceData.Amount) || 0;
    let discount = 0;
    let total = subtotal;

    if (
      invoiceData.DiscountType === "PERCENTAGE" &&
      invoiceData.DiscountPercentage
    ) {
      const percentage = parseFloat(invoiceData.DiscountPercentage);
      discount = (subtotal * percentage) / 100;
      total = Math.max(0, subtotal - discount);
    } else if (invoiceData.DiscountAmount) {
      const amount = Math.max(0, parseFloat(invoiceData.DiscountAmount));
      discount = amount;
      total = Math.max(0, subtotal - amount);
    }

    return {
      subtotal: formatAmount(subtotal),
      discount: formatAmount(discount.toFixed(2)),
      total: formatAmount(total.toFixed(2)),
    };
  };

  // Compute the final total after any discount for an arbitrary invoice row
  // (used by the "Other Invoices" quick list). Amount is the subtotal;
  // discounts live in the discount fields.
  const getInvoiceEffectiveTotal = (invoice) => {
    const subtotal = parseFloat(invoice?.Amount) || 0;
    let discount = 0;

    if (invoice?.DiscountType === "PERCENTAGE" && invoice?.DiscountPercentage) {
      discount = (subtotal * parseFloat(invoice.DiscountPercentage)) / 100;
    } else if (invoice?.DiscountAmount) {
      discount = Math.max(0, parseFloat(invoice.DiscountAmount));
    }

    return Math.max(0, subtotal - discount);
  };

  // Payment status for an arbitrary invoice row (used by the "Other Invoices"
  // quick list). Mirrors the main invoice's invoicePaymentStatus logic.
  const getOtherInvoicePaymentStatus = (invoice) => {
    if (invoice?.PaymentReceivedDate) return "Paid";
    if (
      invoice?.DueDate &&
      dayjs().diff(dayjs(invoice.DueDate), "day") > 30
    ) {
      return "Overdue";
    }
    return "Not Paid";
  };

  const discountValues = getDiscountValues();

  const currentInvoiceNumberDisplay = invoiceData?.Number || "N/A";
  const latestInvoiceNumberDisplay = latestInvoiceNumber ?? "N/A";
  const currentInvoiceNumberNumeric = Number.parseInt(invoiceData?.Number, 10);
  const latestInvoiceNumberNumeric = Number.parseInt(latestInvoiceNumber, 10);

  const hasCurrentInvoiceNumber = Number.isFinite(currentInvoiceNumberNumeric);
  const hasLatestInvoiceNumber = Number.isFinite(latestInvoiceNumberNumeric);
  const isAtFirstInvoiceNumber =
    hasCurrentInvoiceNumber && currentInvoiceNumberNumeric <= 1;
  const isAtLatestInvoiceNumber =
    hasCurrentInvoiceNumber &&
    hasLatestInvoiceNumber &&
    currentInvoiceNumberNumeric >= latestInvoiceNumberNumeric;

  const goToAdjacentInvoiceByNumber = useCallback(
    async (direction) => {
      if (isResolvingInvoiceNavigation) return;

      if (!hasCurrentInvoiceNumber) {
        showSnackbar("Current invoice number is not valid.", theme, "warning");
        return;
      }

      if (direction !== "next" && direction !== "previous") return;
      setIsResolvingInvoiceNavigation(true);

      try {
        const response = await api.get("/invoices/adjacentByNumber", {
          params: {
            number: currentInvoiceNumberNumeric,
            direction,
          },
        });

        const targetInvoiceId = response?.data?.invoice?.id;
        if (!targetInvoiceId) {
          showSnackbar(
            direction === "next"
              ? "No next invoice was found."
              : "No previous invoice was found.",
            theme,
            "warning",
          );
          return;
        }

        navigate(`/invoices/${targetInvoiceId}`, {
          state: { ComingFrom: `/invoices/${id}` },
        });
      } catch (error) {
        if (error?.response?.status === 404) {
          showSnackbar(
            direction === "next"
              ? "No next invoice was found."
              : "No previous invoice was found.",
            theme,
            "warning",
          );
          return;
        }

        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to navigate to invoice.";
        handleErrorDialog(message, theme);
      } finally {
        setIsResolvingInvoiceNavigation(false);
      }
    },
    [
      handleErrorDialog,
      hasCurrentInvoiceNumber,
      id,
      currentInvoiceNumberNumeric,
      isResolvingInvoiceNavigation,
      navigate,
      showSnackbar,
      theme,
    ],
  );

  const goToClientProfile = useCallback(() => {
    if (!invoiceClientId) return;

    navigate(`/clients/${invoiceClientId}`, {
      state: { ComingFrom: `/invoices/${id}` },
    });
  }, [invoiceClientId, navigate, id]);

  const openMarkPaidDialog = () => {
    setMarkPaidDate(dayjs());
    setMarkPaidDialogOpen(true);
  };

  const closeMarkPaidDialog = () => {
    if (isMarkPaidSubmitting) return;
    setMarkPaidDialogOpen(false);
  };

  const markServicesPaidForInvoice = async (services = []) => {
    if (!services.length) return;

    await Promise.all(
      services.map((service) =>
        api.post(`/services/markServicePaid`, {
          data: {
            id: service.id,
            Paid: true,
          },
        }),
      ),
    );
  };

  const handleConfirmMarkPaid = async () => {
    if (!invoiceData?.id) return;

    const paymentDate = (markPaidDate || dayjs()).format("YYYY-MM-DD");

    setIsMarkPaidSubmitting(true);

    try {
      await api.put("/invoices/markPaid", {
        invoiceIds: [invoiceData.id],
        paymentDate,
      });

      await markServicesPaidForInvoice(invoiceData.Services || []);

      triggerPageRefresh("Invoice marked as paid", theme);
      closeMarkPaidDialog();
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to mark invoice as paid.";
      handleErrorDialog(message, theme);
    } finally {
      setIsMarkPaidSubmitting(false);
    }
  };

  const invoicePaymentStatus = useMemo(() => {
    if (invoiceData.PaymentReceivedDate) return "Paid";
    if (
      invoiceData.DueDate &&
      dayjs().diff(dayjs(invoiceData.DueDate), "day") > 30
    ) {
      return "Overdue";
    }
    return "Not Paid";
  }, [invoiceData.PaymentReceivedDate, invoiceData.DueDate]);

  const openOneDriveLink = () => {
    const financialRecordsBaseUrl = `https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FComm%2E%20Essentials%20Operations%2FFinancial%20Records`;

    // ServicesDate is the documented contract; fall back to the invoice Date
    // when it's missing so the OneDrive year/month folder is never "today".
    const invoiceDate = dayjs(invoiceData.ServicesDate || invoiceData.Date);
    const year = invoiceDate.format("YYYY");
    const month = invoiceDate.format("MMM");
    const monthAbbrev = month.substring(0, 3);

    // Determine which OneDrive folder to open:
    // - Unpaid invoices: the PDF is still in the "Invoiced" folder.
    // - Paid within the last 24 hours of being marked paid: the PDF is
    //   still in the "Invoiced" folder (the team hasn't moved it yet).
    // - Paid more than 24 hours ago: the PDF has been moved to "Paid".
    const isPaid = Boolean(invoiceData.PaymentReceivedDate);
    const markedPaidOn = invoiceData.PaymentMarkedPaidOn
      ? dayjs(invoiceData.PaymentMarkedPaidOn)
      : null;
    const paidWithin24Hours =
      markedPaidOn &&
      dayjs().diff(markedPaidOn, "hour", true) < 24;

    let oneDriveLink;
    if (isPaid && !paidWithin24Hours) {
      // Paid more than 24 hours ago — PDF is in the Paid folder.
      oneDriveLink = `${financialRecordsBaseUrl}/Paid`;
    } else {
      // Unpaid, or paid within the last 24 hours — PDF is (still) in the
      // Invoiced folder.
      oneDriveLink = `${financialRecordsBaseUrl}/Invoiced/${year}/${monthAbbrev}`;
    }

    window.open(oneDriveLink, "_blank");
  };
  const deleteService = () => {
    api
      .post("/invoices/deleteInvoice", {
        invoiceId: invoiceData.id,
        changedBy: currentUser.id,
        changedOn: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      })
      .then(() => {
        triggerPageRefresh("Invoice Deleted Sucessfully", theme);
        const rawNavBackTo =
          overlayData?.ComingFrom || data?.ComingFrom || "/invoices";
        const normalizedNavBackTo = String(rawNavBackTo).startsWith("/")
          ? String(rawNavBackTo)
          : `/${rawNavBackTo}`;

        navigate(normalizedNavBackTo);
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete invoice.";
        handleErrorDialog(message, theme);
      });
  };

  // Parse address string into display lines, preserving newline-separated format.
  const parseAddress = (addressString) => {
    const normalizeAddressPart = (value) => {
      const normalized = String(value || "").trim();
      if (!normalized || normalized.toUpperCase() === "N/A") return null;
      return normalized;
    };

    if (!addressString) {
      return { line1: "", line2: "", zipCode: "" };
    }

    const normalized = String(addressString).replace(/\r\n?/g, "\n").trim();

    // Legacy payloads may contain multiple addresses separated with pipe.
    const primaryAddress = normalized.includes(" | ")
      ? normalized.split(" | ")[0].trim()
      : normalized;

    const lines = primaryAddress
      .split("\n")
      .map((part) => normalizeAddressPart(part))
      .filter(Boolean);

    if (lines.length >= 3) {
      return {
        line1: lines[0],
        line2: lines[1],
        zipCode: lines.slice(2).join(" "),
      };
    }

    if (lines.length === 2) {
      return {
        line1: lines[0],
        line2: "",
        zipCode: lines[1],
      };
    }

    // Legacy addresses may still be stored as a single comma-delimited line.
    const commaParts = primaryAddress
      .split(",")
      .map((part) => normalizeAddressPart(part))
      .filter(Boolean);

    if (commaParts.length >= 3) {
      return {
        line1: commaParts[0],
        line2: commaParts.slice(1, -1).join(", "),
        zipCode: commaParts[commaParts.length - 1],
      };
    }

    if (commaParts.length === 2) {
      return {
        line1: commaParts[0],
        line2: "",
        zipCode: commaParts[1],
      };
    }

    return {
      line1: normalizeAddressPart(primaryAddress) || "",
      line2: "",
      zipCode: "",
    };
  };

  const getInvoiceClientAddress = () => {
    // The invoice keeps its OWN address snapshot (Address TEXT, newline-joined
    // by the server). Prefer it over the client's current address so edits made
    // on the invoice are reflected here — the invoice must not track the
    // client's live AddressRecord (0.7.12 regression fix).
    const invoiceAddress = String(invoiceData.Address || "").trim();
    if (invoiceAddress && invoiceAddress.toUpperCase() !== "N/A") {
      return parseAddress(invoiceAddress);
    }

    // Fall back to the client's current address only when the invoice has no
    // snapshot of its own (e.g., legacy invoices created before snapshots).
    if (
      structuredAddressLine1 ||
      structuredAddressLine2 ||
      structuredAddressLine3
    ) {
      return {
        line1: structuredAddressLine1,
        line2: structuredAddressLine2,
        zipCode: structuredAddressLine3,
      };
    }

    return parseAddress(invoiceData.Client?.MainAddress);
  };

  // Convert duration string (HH:MM:SS) to decimal hours with proper formatting
  const convertDurationToDecimal = (durationString) => {
    if (!durationString) return "0 hours";

    const formatDecimalHours = (decimalHours) => {
      const rounded = Math.round(decimalHours * 100) / 100;
      const formattedHours =
        rounded % 1 === 0 ? Math.floor(rounded) : rounded.toFixed(2);
      const unit = parseFloat(formattedHours) === 1 ? "hour" : "hours";
      return `${formattedHours} ${unit}`;
    };

    if (typeof durationString === "number" && Number.isFinite(durationString)) {
      return formatDecimalHours(durationString);
    }

    if (typeof durationString !== "string") return "0 hours";

    const normalizedDuration = durationString.trim();
    const numericDuration = parseFloat(normalizedDuration);
    if (
      normalizedDuration !== "" &&
      !normalizedDuration.includes(":") &&
      Number.isFinite(numericDuration)
    ) {
      return formatDecimalHours(numericDuration);
    }

    const parts = normalizedDuration.split(":");
    if (parts.length === 3) {
      const hours = parseInt(parts[0]) || 0;
      const minutes = parseInt(parts[1]) || 0;
      const seconds = parseInt(parts[2]) || 0;
      const decimalHours = hours + minutes / 60 + seconds / 3600;

      return formatDecimalHours(decimalHours);
    }
    return normalizedDuration;
  };

  // `engine` is "text" for a normal click (default) or "raster" for the
  // legacy engine, fired by a 3-second long-press on the Export PDF buttons.
  const handlePrintInvoiceJsPdfLinks = async (engine = "text") => {
    if (isPrintBlockedByMissingPaymentLink) {
      showSnackbar(
        "Payment Link is required before exporting PDF for credit card clients.",
        theme,
        "error",
      );
      return;
    }

    if (!invoiceRef.current) return;
    setIsPrinting(true);
    setPrintStatus(
      engine === "raster" ? "Preparing legacy PDF…" : "Preparing PDF…",
    );
    setPrintProgress(0);

    // Store original scale and remove it temporarily for accurate PDF capture
    const originalScale = invoiceScaleWrapperRef.current?.style.transform;
    if (invoiceScaleWrapperRef.current) {
      invoiceScaleWrapperRef.current.style.transform = "scale(1)";
    }

    try {
      // Shared pipeline (invoicePdf.js — text engine, engine-agnostic link
      // overlays). It handles the capture-time border-radius zeroing the old
      // inline block did manually, and reports per-page status here.
      const blob = await generateInvoicePdfBlob(invoiceRef.current, {
        scale: 8,
        engine,
        onProgress: (status, progress) => {
          setPrintStatus(status);
          setPrintProgress(progress);
        },
      });

      setPrintStatus("Saving PDF…");
      setPrintProgress(100);

      const clientName =
        invoiceData.ClientName || invoiceData.Client?.Account || "Client";
      const detailedSuffix = detailedOPI && hasOPIServices ? " Detailed" : "";
      downloadBlob(
        blob,
        `Invoice ${invoiceData.Number} ${clientName}${detailedSuffix}.pdf`,
      );
      setPrintStatus("Done");
    } catch (err) {
      console.error("PDF print failed", err);
      setPrintStatus("Failed to generate PDF");
    } finally {
      // Restore original scale transform
      if (invoiceScaleWrapperRef.current && originalScale) {
        invoiceScaleWrapperRef.current.style.transform = originalScale;
      }
      setIsPrinting(false);
    }
  };

  // Two independent long-press detectors — one per "Export PDF" button
  // (desktop action menu + mobile variant). Quick click exports with the
  // text engine; holding for 3 seconds exports with the legacy raster
  // engine instead.
  const desktopExportLongPress = useLongPress({
    onTrigger: () => handlePrintInvoiceJsPdfLinks("raster"),
  });
  const mobileExportLongPress = useLongPress({
    onTrigger: () => handlePrintInvoiceJsPdfLinks("raster"),
  });

  // Convert a Blob to a base64 string (data URL prefix stripped).
  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        const base64 =
          typeof result === "string" ? result.split(",")[1] || result : "";
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });

  // Creates an Outlook draft (via the server's Graph integration) in the
  // shared operations@ mailbox, addressed to the invoicer + group CCs, with
  // the invoice PDF attached. The draft is for review before sending.
  const handleDraftEmail = async () => {
    if (!invoiceRef.current || isDraftingEmail) return;
    setIsDraftingEmail(true);
    try {
      // Temporarily remove the preview scale so the capture is full-size.
      const originalScale = invoiceScaleWrapperRef.current?.style.transform;
      if (invoiceScaleWrapperRef.current) {
        invoiceScaleWrapperRef.current.style.transform = "scale(1)";
      }
      try {
        const pdfBlob = await generateInvoicePdfBlob(invoiceRef.current, {
          scale: 8,
        });
        const contentBytes = await blobToBase64(pdfBlob);
        const clientName =
          invoiceData.ClientName || invoiceData.Client?.Account || "Client";
        const { data } = await api.post(
          `/invoices/${invoiceData.id}/draftEmail`,
          {
            attachment: {
              name: `Invoice ${invoiceData.Number} ${clientName}.pdf`,
              contentType: "application/pdf",
              contentBytes,
            },
          },
        );
        showSnackbar(
          "Draft created in the operations@ mailbox",
          theme,
          "success",
          theme.palette.background.default,
          data?.webLink
            ? {
                label: "Open Draft",
                onClick: () => window.open(data.webLink, "_blank"),
              }
            : null,
        );
      } finally {
        // Restore original scale transform
        if (invoiceScaleWrapperRef.current && originalScale) {
          invoiceScaleWrapperRef.current.style.transform = originalScale;
        }
      }
    } catch (err) {
      // 400 (e.g. invoicer has no email) and 502 (Graph failure) both carry
      // an actionable message from the server — surface it as-is.
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create draft";
      showSnackbar(message, theme, "error");
    } finally {
      setIsDraftingEmail(false);
    }
  };

  return (
    <>
      <Box
        p="1rem 1rem 0 1rem"
        bgcolor={theme.palette.background.alt}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        }}
      >
        {isMobile ? (
          // Mobile: Stacked layout (Preview + Actions visible together)
          <Box
            bgcolor={theme.palette.background.alt}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {/* Collapsible Details Section */}
            <Box
              onClick={() => setTabValue(tabValue === 0 ? -1 : 0)}
              sx={{
                p: "1rem",
                backgroundColor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Invoice Details
              </Typography>
              {tabValue === 0 ? (
                <ExpandLessRoundedIcon />
              ) : (
                <ExpandMoreRoundedIcon />
              )}
            </Box>

            {tabValue === 0 && (
              <Box
                sx={{
                  p: "1rem",
                  backgroundColor: theme.palette.background.default,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Invoice Number
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invoiceData.Number || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Client
                    </Typography>
                    <Typography
                      variant="body1"
                      onClick={goToClientProfile}
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                        cursor: invoiceClientId ? "pointer" : "default",
                        textDecoration: "none",
                        "&:hover": invoiceClientId
                          ? { textDecoration: "underline" }
                          : {},
                      }}
                    >
                      {invoiceClientName}
                    </Typography>
                  </Box>

                  {invoiceInvoicer && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Invoicer
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {invoiceInvoicer.Name || "N/A"}
                      </Typography>
                      {invoiceInvoicer.Email && (
                        <Typography variant="body2" color="text.secondary">
                          {invoiceInvoicer.Email}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {invoiceCcEmails.length > 0 && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        CC
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {invoiceCcEmails.join(", ")}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Issue Date
                    </Typography>
                    <Typography variant="body1">
                      {invoiceData.Date
                        ? dayjs(invoiceData.Date).format("MMM DD, YYYY")
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Due Date
                    </Typography>
                    <Typography variant="body1">
                      {invoiceData.DueDate
                        ? dayjs(invoiceData.DueDate).format("MMM DD, YYYY")
                        : "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {discountValues.total}
                    </Typography>

                    {/* Discount Display */}
                    {((discountValues.discount &&
                      discountValues.discount !== "N/A") ||
                      invoiceData.DiscountNote) && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontWeight: 600 }}
                        >
                          {invoiceData.DiscountType === "PERCENTAGE"
                            ? `Discount (${invoiceData.DiscountPercentage}%):`
                            : invoiceData.DiscountType === "FIXED"
                              ? "Discount Amount:"
                              : "Discount:"}
                        </Typography>
                        <Typography variant="body1">
                          {formatAmount(discountValues.discount)}
                          {invoiceData.DiscountNote && (
                            <Box sx={{ ml: 1 }}>
                              <Typography
                                component="span"
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontWeight: 500,
                                }}
                              >
                                ({invoiceData.DiscountNote})
                              </Typography>
                            </Box>
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Payment Status
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "12px",
                        backgroundColor: (() => {
                          if (invoicePaymentStatus === "Paid")
                            return theme.palette.primary.main;
                          if (invoicePaymentStatus === "Overdue")
                            return theme.palette.error.main;
                          return theme.palette.warning.dark;
                        })(),
                        mt: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff",
                          fontWeight: "bold",
                          fontSize: "13px",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {invoicePaymentStatus}
                      </Typography>
                    </Box>
                  </Box>

                  {invoiceData.PaymentReceivedDate && (
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Paid On
                      </Typography>
                      <Typography variant="body1">
                        {dayjs(invoiceData.PaymentReceivedDate).format(
                          "MMM DD, YYYY",
                        )}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {/* Invoice Preview */}
            <Box
              sx={{
                p: "1rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                overflow: "auto",
              }}
            >
              <Box
                ref={invoiceScaleWrapperRef}
                sx={{
                  transform: "scale(0.65)",
                  transformOrigin: "top center",
                  transition: "transform 0.3s ease",
                }}
              >
                <Invoice
                  ref={invoiceRef}
                  pagePadding={PAGE_PADDING}
                  invoiceId={invoiceData.id}
                  invoiceNumber={invoiceData.Number}
                  dateOfIssue={dayjs(invoiceData.Date).format("MM-DD-YYYY")}
                  dueDate={dayjs(invoiceData.DueDate).format("MM-DD-YYYY")}
                  paidDate={
                    invoiceData.PaymentReceivedDate
                      ? dayjs(invoiceData.PaymentReceivedDate).format(
                          "MM-DD-YYYY",
                        )
                      : null
                  }
                  clientId={invoiceClientId}
                  clientName={invoiceClientName}
                  clientAddress={getInvoiceClientAddress()}
                  purchaseOrders={invoiceData.PurchaseOrder}
                  items={displayItems}
                  subtotal={discountValues.subtotal}
                  discount={discountValues.discount}
                  total={discountValues.total}
                  notes={showNote ? invoiceData.Note : null}
                  terms={invoiceTerms}
                  itemsPerFirstPage={firstPageRows}
                  itemsPerPage={secondPageRows}
                  rowSpacing={rowSpacing + "cm"}
                  splitMileage={isMileageSplitSelected}
                  paymentLink={invoiceData.PaymentLink}
                />
              </Box>
            </Box>

            <Divider />

            {/* Actions Section */}
            <Box sx={{ p: "1rem", overflowY: "auto" }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ textAlign: "center", mb: 0.5 }}
              >
                Navigation
              </Typography>
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <IconButton
                  size="medium"
                  onClick={() => goToAdjacentInvoiceByNumber("previous")}
                  disabled={
                    isResolvingInvoiceNavigation || isAtFirstInvoiceNumber
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ChevronLeftRoundedIcon fontSize="medium" />
                </IconButton>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, lineHeight: 1.1 }}
                >
                  {`${currentInvoiceNumberDisplay} / ${latestInvoiceNumberDisplay}`}
                </Typography>
                <IconButton
                  size="medium"
                  onClick={() => goToAdjacentInvoiceByNumber("next")}
                  disabled={
                    isResolvingInvoiceNavigation || isAtLatestInvoiceNumber
                  }
                  sx={{ color: theme.palette.primary.main }}
                >
                  <ChevronRightRoundedIcon fontSize="medium" />
                </IconButton>
              </Stack>

              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Invoice Actions
              </Typography>

              <Box width="100%" sx={{ mt: 2 }}>
                {invoiceData.PaymentReceivedDate ? null : (
                  <ListItem key="MarkAsPaid" disablePadding>
                    <ListItemButton
                      sx={{
                        color: theme.palette.primary.main,
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: theme.palette.action.hover,
                        },
                      }}
                      onClick={() => {
                        openMarkPaidDialog();
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          mr: "-1rem",
                          color: theme.palette.primary.main,
                        }}
                      >
                        <CreditScoreRoundedIcon
                          sx={{
                            fontSize: 28,
                            color: theme.palette.primary.main,
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText primary="Mark As Paid" />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem key="EditInvoice" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => {
                      const data = {
                        Action: OVERLAY_NAMES.INVOICES_ACTION_EDIT,
                        invoiceData: invoiceData,
                      };
                      setOverlayData(data);
                      setOverlayName(OVERLAY_NAMES.INVOICES_ACTION_EDIT);
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <EditRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Edit Invoice" />
                  </ListItemButton>
                </ListItem>

                <ListItem key="PrintJsPdfLinks" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      // Long-press safety: suppress the iOS callout and
                      // text selection during the 3-second hold.
                      WebkitTouchCallout: "none",
                      userSelect: "none",
                    }}
                    disabled={isPrinting || isPrintBlockedByMissingPaymentLink}
                    {...desktopExportLongPress.handlers}
                    onClick={() => {
                      if (desktopExportLongPress.consumeLongPress()) return;
                      handlePrintInvoiceJsPdfLinks();
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <PictureAsPdfRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary="Export PDF"
                      secondary={
                        desktopExportLongPress.isHolding
                          ? "Keep holding for legacy export…"
                          : isPrintBlockedByMissingPaymentLink
                          ? "Add Payment Link first"
                          : null
                      }
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem key="DraftEmail" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    disabled={isDraftingEmail}
                    onClick={() => {
                      handleDraftEmail();
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <MailOutlineRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        isDraftingEmail ? "Creating Draft…" : "Draft Email"
                      }
                      secondary="Draft in operations@ mailbox for review before sending"
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem key="DeleteAssignment" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => {
                      openDialog({
                        theme: theme,
                        title: "Delete Invoice",
                        message: "Do you really wish to delete this invoice?",
                        customContent: null,
                        tertiaryAction: null,
                        secondaryAction: "Close",
                        primaryAction: "Delete",
                        tertiaryOnClick: null,
                        secondaryOnClick: () => {
                          closeDialog();
                        },
                        primaryOnClick: () => {
                          deleteService();
                          closeDialog();
                        },
                      });
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <DeleteRoundedIcon
                        sx={{
                          fontSize: 28,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Delete Invoice" />
                  </ListItemButton>
                </ListItem>

                <ListItem key="OneDrive" disablePadding>
                  <ListItemButton
                    sx={{
                      color: theme.palette.primary.main,
                      borderRadius: "8px",
                      border: `1px solid ${theme.palette.divider}`,
                      mb: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                    onClick={() => openOneDriveLink()}
                  >
                    <ListItemIcon
                      sx={{
                        mr: "-1rem",
                        color: theme.palette.primary.main,
                      }}
                    >
                      <CloudIcon
                        sx={{
                          fontSize: 24,
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Open One Drive Link" />
                  </ListItemButton>
                </ListItem>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  Invoices Options
                </Typography>

                <ListItem key="SplitCostsAll" disablePadding sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={allCostsSelected}
                        indeterminate={anyCostSelected && !allCostsSelected}
                        onChange={(e) => setAllCostsSelected(e.target.checked)}
                        disabled={availableSplitCostOptions.length === 0}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Costs"
                    sx={{
                      color:
                        availableSplitCostOptions.length > 0
                          ? theme.palette.primary.main
                          : theme.palette.text.disabled,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                {availableSplitCostOptions.map((costOption) => (
                  <ListItem
                    key={`SplitCost-${costOption.key}`}
                    disablePadding
                    sx={{ pl: "1.6rem" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(splitCostSelections[costOption.key])}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSplitCostSelections((prev) => ({
                              ...prev,
                              [costOption.key]: checked,
                            }));
                          }}
                          sx={{
                            color: theme.palette.primary.main,
                            "&.Mui-checked": {
                              color: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={costOption.label}
                      sx={{
                        color: theme.palette.primary.main,
                        width: "100%",
                        m: 0,
                        ml: "0.5rem",
                      }}
                    />
                  </ListItem>
                ))}

                <ListItem key="SplitLanguages" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitLanguages}
                        disabled={!canSplitLanguages}
                        onChange={(e) => setSplitLanguages(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Split Languages"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="SplitLanguagesMinimum" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitLanguagesMinimum}
                        disabled={!canSplitLanguages}
                        onChange={(e) =>
                          setSplitLanguagesMinimum(e.target.checked)
                        }
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Split Languages Minimum"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="SplitSimultaneous" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={splitSimultaneous}
                        onChange={(e) => setSplitSimultaneous(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Split Simultaneous"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <ListItem key="DetailedOPI" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={detailedOPI}
                        onChange={(e) => setDetailedOPI(e.target.checked)}
                        disabled={!hasOPIServices}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Detailed OPI"
                    sx={{
                      color: hasOPIServices
                        ? theme.palette.primary.main
                        : theme.palette.text.disabled,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                      ...(hasMultiLangOPI && !detailedOPI
                        ? DETAILED_OPI_BLINK
                        : {}),
                    }}
                  />
                </ListItem>

                <ListItem key="ShowNote" disablePadding>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showNote}
                        onChange={(e) => setShowNote(e.target.checked)}
                        sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Show Note"
                    sx={{
                      color: theme.palette.primary.main,
                      width: "100%",
                      m: 0,
                      ml: "0.5rem",
                    }}
                  />
                </ListItem>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.7,
                    },
                  }}
                  onClick={() => setShowPrintOptions(!showPrintOptions)}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Print Options
                  </Typography>
                  {showPrintOptions ? (
                    <ExpandLessRoundedIcon sx={{ ml: 1 }} />
                  ) : (
                    <ExpandMoreRoundedIcon sx={{ ml: 1 }} />
                  )}
                </Box>

                {showPrintOptions && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ minWidth: "150px" }}>
                        First Page Rows:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={firstPageRows}
                        onChange={(e) =>
                          setFirstPageRows(Number(e.target.value))
                        }
                        inputProps={{ min: 1, max: 20 }}
                        sx={{ width: "100px" }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ minWidth: "150px" }}>
                        Other Page Rows:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={secondPageRows}
                        onChange={(e) =>
                          setSecondPageRows(Number(e.target.value))
                        }
                        inputProps={{ min: 1, max: 20 }}
                        sx={{ width: "100px" }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Typography variant="body1" sx={{ minWidth: "150px" }}>
                        Row Spacing:
                      </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={rowSpacing}
                        onChange={(e) => setRowSpacing(Number(e.target.value))}
                        inputProps={{ step: 0.005, min: 0 }}
                        sx={{ width: "100px" }}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          // Desktop: 3-Column layout
          <Box
            bgcolor={theme.palette.background.alt}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              overflow: "auto",
              maxHeight: "100%",
            }}
          >
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {/* Sidebar with Invoice Details and Quick Links */}
              <Grid
                size={{ xs: 12, sm: 12, md: "auto" }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "280px",
                  maxWidth: "280px",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      p: ".75rem 0",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Invoice Details Section */}
                    <Grid size={{ xs: 12 }} sx={{ mb: 3 }}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom>
                        Invoice Details
                      </Typography>

                      <Stack spacing={2} sx={{ mt: 2 }}>
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Invoice Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {invoiceData.Number || "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Client
                          </Typography>
                          <Typography
                            variant="body1"
                            onClick={goToClientProfile}
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              cursor: invoiceClientId ? "pointer" : "default",
                              textDecoration: "none",
                              "&:hover": invoiceClientId
                                ? { textDecoration: "underline" }
                                : {},
                            }}
                          >
                            {invoiceClientName}
                          </Typography>
                        </Box>

                        {invoiceInvoicer && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Invoicer
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {invoiceInvoicer.Name || "N/A"}
                            </Typography>
                            {invoiceInvoicer.Email && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {invoiceInvoicer.Email}
                              </Typography>
                            )}
                          </Box>
                        )}

                        {invoiceCcEmails.length > 0 && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              CC
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {invoiceCcEmails.join(", ")}
                            </Typography>
                          </Box>
                        )}

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Issue Date
                          </Typography>
                          <Typography variant="body1">
                            {invoiceData.Date
                              ? dayjs(invoiceData.Date).format("MMM DD, YYYY")
                              : "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Due Date
                          </Typography>
                          <Typography variant="body1">
                            {invoiceData.DueDate
                              ? dayjs(invoiceData.DueDate).format(
                                  "MMM DD, YYYY",
                                )
                              : "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Amount
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {discountValues.total}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            Payment Status
                          </Typography>
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              px: 1.5,
                              py: 0.5,
                              borderRadius: "8px",
                              backgroundColor: (() => {
                                if (invoicePaymentStatus === "Paid")
                                  return theme.palette.primary.main;
                                if (invoicePaymentStatus === "Overdue")
                                  return theme.palette.error.main;
                                return theme.palette.warning[400];
                              })(),
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "13px",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {invoicePaymentStatus}
                            </Typography>
                          </Box>
                        </Box>

                        {invoiceData.PaymentReceivedDate && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontWeight: 600 }}
                            >
                              Paid On
                            </Typography>
                            <Typography variant="body1">
                              {dayjs(invoiceData.PaymentReceivedDate).format(
                                "MMM DD, YYYY",
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Quick Links Section */}
                    <Grid
                      size={{ xs: 12 }}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                        minHeight: 0,
                      }}
                    >
                      <Box
                        onScroll={handleOtherInvoicesScroll}
                        sx={{
                          mt: 0,
                          flex: 1,
                          minHeight: 0,
                          overflowY: "auto",
                          pr: 0.5,
                        }}
                      >
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          Services ({invoiceData.Services?.length || 0})
                        </Typography>

                        <Stack spacing={1} sx={{ mt: 2 }}>
                          {invoiceData.Services?.slice(0, 15).map((service) => {
                            const servicePath =
                              service.ServiceCategory === "Interpretation"
                                ? `/interpretations/${service.id}`
                                : service.ServiceCategory === "Translation"
                                  ? `/translations/${service.id}`
                                  : `/otherServices/${service.id}`;

                            return (
                              <Box
                                key={service.id}
                                component="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(servicePath);
                                }}
                                sx={{
                                  textDecoration: "none",
                                  color: theme.palette.primary.main,
                                  cursor: "pointer",
                                  p: 1,
                                  borderRadius: 1,
                                  transition: "background-color 0.2s",
                                  width: "100%",
                                  textAlign: "left",
                                  border: "none",
                                  background: "transparent",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  #{service.ProjectNumber}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  {dayjs(service.Date).format("MMM DD, YYYY")} •{" "}
                                  {service.ServiceCategory}
                                </Typography>
                              </Box>
                            );
                          })}
                          {invoiceData.Services?.length > 15 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ pl: 1, pt: 1 }}
                            >
                              + {invoiceData.Services.length - 15} more services
                            </Typography>
                          )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                          Other Invoices
                        </Typography>

                        {isOtherInvoicesInitialLoading ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 1 }}
                          >
                            Loading other invoices...
                          </Typography>
                        ) : visibleOtherInvoices.length === 0 ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 1 }}
                          >
                            No other invoices found for this client.
                          </Typography>
                        ) : (
                          <Stack spacing={1}>
                            {visibleOtherInvoices.map((otherInvoice) => (
                              <Box
                                key={otherInvoice.id}
                                onClick={() => {
                                  const stateData = {
                                    ComingFrom: `/invoices/${id}`,
                                  };
                                  navigate(`/invoices/${otherInvoice.id}`, {
                                    state: stateData,
                                  });
                                }}
                                sx={{
                                  textDecoration: "none",
                                  color: theme.palette.primary.main,
                                  cursor: "pointer",
                                  p: 1,
                                  borderRadius: 1,
                                  transition: "background-color 0.2s",
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                    textDecoration: "none",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    #{otherInvoice.Number || "N/A"}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "8px",
                                      backgroundColor: (() => {
                                        const status =
                                          getOtherInvoicePaymentStatus(
                                            otherInvoice,
                                          );
                                        if (status === "Paid")
                                          return theme.palette.primary.main;
                                        if (status === "Overdue")
                                          return theme.palette.error.main;
                                        return theme.palette.warning[400];
                                      })(),
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#fff",
                                        fontWeight: "bold",
                                        fontSize: "13px",
                                        letterSpacing: "0.5px",
                                      }}
                                    >
                                      {getOtherInvoicePaymentStatus(
                                        otherInvoice,
                                      )}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block" }}
                                >
                                  {otherInvoice.Date
                                    ? dayjs(otherInvoice.Date).format(
                                        "MMM DD, YYYY",
                                      )
                                    : "N/A"}
                                  {" • "}
                                  {formatAmount(
                                    getInvoiceEffectiveTotal(otherInvoice),
                                  )}
                                </Typography>
                              </Box>
                            ))}

                            {isOtherInvoicesLoadingMore && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ px: 1, py: 0.5 }}
                              >
                                Loading more...
                              </Typography>
                            )}

                            {!isOtherInvoicesLoadingMore &&
                              otherInvoicesVisibleCount <
                                otherInvoices.length && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ px: 1, py: 0.5 }}
                                >
                                  Scroll for more
                                </Typography>
                              )}
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                  </Box>
                </Box>
              </Grid>

              <Divider
                orientation={isSmallScreen ? "horizontal" : "vertical"}
                flexItem
              />

              <Grid
                size={{ xs: 12, sm: 12, md: "grow" }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "400px",
                  maxWidth: "400px",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflowY: "auto",
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        overflow: "auto",
                        py: ".5rem",
                        bgcolor: theme.palette.background.alt,
                      }}
                    >
                      <Invoice
                        ref={invoiceRef}
                        pagePadding={PAGE_PADDING}
                        invoiceId={invoiceData.id}
                        invoiceNumber={invoiceData.Number}
                        dateOfIssue={dayjs(invoiceData.Date).format(
                          "MM-DD-YYYY",
                        )}
                        dueDate={dayjs(invoiceData.DueDate).format(
                          "MM-DD-YYYY",
                        )}
                        paidDate={
                          invoiceData.PaymentReceivedDate
                            ? dayjs(invoiceData.PaymentReceivedDate).format(
                                "MM-DD-YYYY",
                              )
                            : null
                        }
                        clientId={invoiceClientId}
                        clientName={invoiceClientName}
                        clientAddress={getInvoiceClientAddress()}
                        purchaseOrders={invoiceData.PurchaseOrder}
                        items={displayItems}
                        subtotal={discountValues.subtotal}
                        discount={discountValues.discount}
                        total={discountValues.total}
                        notes={showNote ? invoiceData.Note : null}
                        terms={invoiceTerms}
                        itemsPerFirstPage={firstPageRows}
                        itemsPerPage={secondPageRows}
                        rowSpacing={rowSpacing + "cm"}
                        splitMileage={isMileageSplitSelected}
                        paymentLink={invoiceData.PaymentLink}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Divider
                orientation={isLargeScreen ? "vertical" : "horizontal"}
                flexItem
              />

              <Grid
                size={{ xs: 12, sm: 12, md: 12, lg: 3 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "240px",
                  maxWidth: "240px",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    overflowY: "auto",
                    flex: 1,
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      p: ".75rem 0",
                      overflowY: "auto",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
                      Navigation
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1.5 }}
                    >
                      <IconButton
                        size="medium"
                        onClick={() => goToAdjacentInvoiceByNumber("previous")}
                        disabled={
                          isResolvingInvoiceNavigation || isAtFirstInvoiceNumber
                        }
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <ChevronLeftRoundedIcon fontSize="medium" />
                      </IconButton>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, lineHeight: 1.1 }}
                      >
                        {`${currentInvoiceNumberDisplay} / ${latestInvoiceNumberDisplay}`}
                      </Typography>
                      <IconButton
                        size="medium"
                        onClick={() => goToAdjacentInvoiceByNumber("next")}
                        disabled={
                          isResolvingInvoiceNavigation ||
                          isAtLatestInvoiceNumber
                        }
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <ChevronRightRoundedIcon fontSize="medium" />
                      </IconButton>
                    </Stack>

                    <Grid size={{ xs: 12 }}>
                      <Typography variant="h5" fontWeight="bold">
                        Invoice Actions
                      </Typography>
                    </Grid>

                    <Box width="100%">
                      {invoiceData.PaymentReceivedDate ? null : (
                        <ListItem key="MarkAsPaid" disablePadding>
                          <ListItemButton
                            sx={{
                              color: theme.palette.primary.main,
                              borderRadius: "8px",
                              border: `1px solid ${theme.palette.divider}`,
                              mb: 1,
                              "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                              },
                            }}
                            onClick={() => {
                              openMarkPaidDialog();
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                mr: "-1rem",
                                color: theme.palette.primary.main,
                              }}
                            >
                              <CreditScoreRoundedIcon
                                sx={{
                                  fontSize: 28,
                                  color: theme.palette.primary.main,
                                }}
                              />
                            </ListItemIcon>
                            <ListItemText primary="Mark As Paid" />
                          </ListItemButton>
                        </ListItem>
                      )}

                      <ListItem key="EditInvoice" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            const data = {
                              Action: OVERLAY_NAMES.INVOICES_ACTION_EDIT,
                              invoiceData: invoiceData,
                            };
                            setOverlayData(data);
                            setOverlayName(OVERLAY_NAMES.INVOICES_ACTION_EDIT);
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <EditRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Edit Invoice" />
                        </ListItemButton>
                      </ListItem>

                      <ListItem key="PrintJsPdfLinks" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                            // Long-press safety: suppress the iOS callout and
                            // text selection during the 3-second hold.
                            WebkitTouchCallout: "none",
                            userSelect: "none",
                          }}
                          disabled={
                            isPrinting || isPrintBlockedByMissingPaymentLink
                          }
                          {...mobileExportLongPress.handlers}
                          onClick={() => {
                            if (mobileExportLongPress.consumeLongPress()) {
                              return;
                            }
                            handlePrintInvoiceJsPdfLinks();
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <PictureAsPdfRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary="Export PDF"
                            secondary={
                              mobileExportLongPress.isHolding
                                ? "Keep holding for legacy export…"
                                : isPrintBlockedByMissingPaymentLink
                                ? "Add Payment Link first"
                                : null
                            }
                          />
                        </ListItemButton>
                      </ListItem>

                      <ListItem key="DeleteAssignment" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => {
                            openDialog({
                              theme: theme,
                              title: "Delete Invoice",
                              message:
                                "Do you really wish to delete this invoice?",
                              customContent: null,
                              tertiaryAction: null,
                              secondaryAction: "Close",
                              primaryAction: "Delete",
                              tertiaryOnClick: null,
                              secondaryOnClick: () => {
                                closeDialog();
                              },
                              primaryOnClick: () => {
                                deleteService();
                                closeDialog();
                              },
                            });
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <DeleteRoundedIcon
                              sx={{
                                fontSize: 28,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Delete Invoice" />
                        </ListItemButton>
                      </ListItem>

                      <ListItem key="OneDrive" disablePadding>
                        <ListItemButton
                          sx={{
                            color: theme.palette.primary.main,
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.divider}`,
                            mb: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                          onClick={() => openOneDriveLink()}
                        >
                          <ListItemIcon
                            sx={{
                              mr: "-1rem",
                              color: theme.palette.primary.main,
                            }}
                          >
                            <CloudIcon
                              sx={{
                                fontSize: 24,
                                color: theme.palette.primary.main,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary="Open One Drive Link" />
                        </ListItemButton>
                      </ListItem>

                      <Divider sx={{ my: 2 }} />
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Invoices Options
                      </Typography>

                      <ListItem
                        key="SplitCostsAll"
                        disablePadding
                        sx={{ mt: 1 }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allCostsSelected}
                              indeterminate={
                                anyCostSelected && !allCostsSelected
                              }
                              onChange={(e) =>
                                setAllCostsSelected(e.target.checked)
                              }
                              disabled={availableSplitCostOptions.length === 0}
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Costs"
                          sx={{
                            color:
                              availableSplitCostOptions.length > 0
                                ? theme.palette.primary.main
                                : theme.palette.text.disabled,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      {availableSplitCostOptions.map((costOption) => (
                        <ListItem
                          key={`SplitCost-${costOption.key}`}
                          disablePadding
                          sx={{ pl: "1.6rem" }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(
                                  splitCostSelections[costOption.key],
                                )}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSplitCostSelections((prev) => ({
                                    ...prev,
                                    [costOption.key]: checked,
                                  }));
                                }}
                                sx={{
                                  color: theme.palette.primary.main,
                                  "&.Mui-checked": {
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              />
                            }
                            label={costOption.label}
                            sx={{
                              color: theme.palette.primary.main,
                              width: "100%",
                              m: 0,
                              ml: "0.5rem",
                            }}
                          />
                        </ListItem>
                      ))}

                      <ListItem key="SplitLanguages" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitLanguages}
                              disabled={!canSplitLanguages}
                              onChange={(e) =>
                                setSplitLanguages(e.target.checked)
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Split Languages"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="SplitLanguagesMinimum" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitLanguagesMinimum}
                              disabled={!canSplitLanguages}
                              onChange={(e) =>
                                setSplitLanguagesMinimum(e.target.checked)
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Split Languages Minimum"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="SplitSimultaneous" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={splitSimultaneous}
                              onChange={(e) =>
                                setSplitSimultaneous(e.target.checked)
                              }
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Split Simultaneous"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>

                      <ListItem key="DetailedOPI" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={detailedOPI}
                              onChange={(e) => setDetailedOPI(e.target.checked)}
                              disabled={!hasOPIServices}
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Detailed OPI"
                          sx={{
                            color: hasOPIServices
                              ? theme.palette.primary.main
                              : theme.palette.text.disabled,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                            ...(hasMultiLangOPI && !detailedOPI
                              ? DETAILED_OPI_BLINK
                              : {}),
                          }}
                        />
                      </ListItem>

                      <ListItem key="ShowNote" disablePadding>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showNote}
                              onChange={(e) => setShowNote(e.target.checked)}
                              sx={{
                                color: theme.palette.primary.main,
                                "&.Mui-checked": {
                                  color: theme.palette.primary.main,
                                },
                              }}
                            />
                          }
                          label="Show Note"
                          sx={{
                            color: theme.palette.primary.main,
                            width: "100%",
                            m: 0,
                            ml: "0.5rem",
                          }}
                        />
                      </ListItem>
                    </Box>

                    <Divider
                      sx={{
                        my: 2,
                      }}
                    />

                    <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.7,
                          },
                        }}
                        onClick={() => setShowPrintOptions(!showPrintOptions)}
                      >
                        <Typography variant="h5" fontWeight="bold">
                          Print Options
                        </Typography>
                        {showPrintOptions ? (
                          <ExpandLessRoundedIcon sx={{ ml: 1 }} />
                        ) : (
                          <ExpandMoreRoundedIcon sx={{ ml: 1 }} />
                        )}
                      </Box>
                    </Grid>

                    {showPrintOptions && (
                      <>
                        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ minWidth: "150px" }}
                            >
                              First Page Rows:
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={firstPageRows}
                              onChange={(e) =>
                                setFirstPageRows(Number(e.target.value))
                              }
                              inputProps={{ min: 1, max: 20 }}
                              sx={{ width: "100px" }}
                            />
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ minWidth: "150px" }}
                            >
                              Other Page Rows:
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={secondPageRows}
                              onChange={(e) =>
                                setSecondPageRows(Number(e.target.value))
                              }
                              inputProps={{ min: 1, max: 20 }}
                              sx={{ width: "100px" }}
                            />
                          </Box>
                        </Grid>

                        <Grid size={{ xs: 12 }} sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{ minWidth: "150px" }}
                            >
                              Row Spacing:
                            </Typography>
                            <TextField
                              type="number"
                              size="small"
                              value={rowSpacing}
                              onChange={(e) =>
                                setRowSpacing(Number(e.target.value))
                              }
                              inputProps={{ step: 0.005, min: 0 }}
                              sx={{ width: "100px" }}
                            />
                          </Box>
                        </Grid>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
      <Dialog
        theme={theme}
        openDialog={markPaidDialogOpen}
        title="Mark As Paid"
        customContent={
          <Stack spacing={2}>
            <Typography variant="body1">
              Mark this invoice as paid and optionally record the payment date.
            </Typography>
            <CEDatePicker
              label="Invoice Paid on"
              value={markPaidDate}
              onChange={(newValue) => setMarkPaidDate(newValue || dayjs())}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Stack>
        }
        secondaryAction="Cancel"
        secondaryOnClick={closeMarkPaidDialog}
        primaryAction={isMarkPaidSubmitting ? "Saving..." : "Yes, Mark Paid"}
        primaryOnClick={handleConfirmMarkPaid}
      />
      {isPrinting && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "rgba(0,0,0,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRadius: "16px",
              boxShadow: theme.shadows[8],
              minWidth: "320px",
              maxWidth: "420px",
              width: "100%",
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            <Typography variant="h4" fontWeight={700} textAlign="center">
              Generating PDF…
            </Typography>
            <Typography variant="body1" textAlign="center">
              {printStatus}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={printProgress}
              sx={{ height: 10, borderRadius: 9999 }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              Please wait, this may take a few seconds.
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default InvoiceProfile;
